import { format } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  Calendar as CalendarIcon,
  CheckCircle,
  CheckCircle2,
  Download,
  FileSearch,
  Info,
  Package,
  Pause,
  Play,
  Ruler,
  Search,
  Wrench,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { useJobCardPriority } from "@features/cx/shared/hooks";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { cn } from "@shared/lib/utils";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Calendar } from "@shared/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { Tabs } from "@shared/ui/tabs";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  useConsumerDetails,
  useDownloadReport,
  useAssignedUnitResources,
  useServiceOrder,
  useUpdateConsumerInstallationStatus,
  useInstallationCompleted,
  useUpdateMeterInstallation,
  useUpdateStatus,
  useUpdateQuantityConnection,
  useConnectionDetail,
} from "../../hooks";
import ApplicationHeader from "./ApplicationHeader";
import { useWorkflow } from "./WorkflowContext";
import WorkflowProgress from "./WorkflowProgress";
import {
  useUtilityServices,
  useUpdateConsumer,
  useGetQuantityWiseResource,
  useQuantityWiseResource,
} from "@features/cx/consumerOnboarding/hooks";
import { CXCxOnboardingApi } from "../../api";
import { normalizeDate } from "@shared/utils/dateUtils";
import UnsavedConnectionsWarningDialog, {
  PendingConnection,
} from "./UnsavedConnectionsWarningDialog";
import { queryClient } from "@shared/api/queries/queryClient";

// Enhanced Form array interface
interface UtilityServiceForm {
  utilityService: string;
  deviceNumber: string;
  latestReading: string;
  readingDate: string;
  selectedConsumer?: any;
  selectedMeter?: any;
}

interface FormData {
  services: UtilityServiceForm[];
}

const InstallationPage = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
  } = useWorkflow();
  const navigate = useNavigate();
  const location = useLocation();
  const { consumerId } = useParams<{ consumerId: string }>();
  const { toast } = useToast();
  console.log("application consumer", application);
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("New Service Connection Details");
  startTracking();
  // State management
  const [installationTypes, setInstallationTypes] = useState<
    Record<string, "schedule" | "assign" | "no-meter">
  >({});
  const [showServiceOrderDetails, setShowServiceOrderDetails] = useState<
    Record<string, boolean>
  >({});
  const [serviceOrderDetails, setServiceOrderDetails] = useState<
    Record<string, any>
  >({});
  const [searchTerm, setSearchTerm] = useState("");
  const [latestReadings, setLatestReadings] = useState<Record<string, string>>(
    {},
  );
  const [readingDates, setReadingDates] = useState<
    Record<string, Date | undefined>
  >({});
  const [frozenMeters, setFrozenMeters] = useState<Record<string, boolean>>({});
  const [deviceNumber, setDeviceNumber] = useState<string>("");
  const [currentSearchValue, setCurrentSearchValue] = useState<string>("");
  const [activeUtilityService, setActiveUtilityService] = useState<string>("");
  const [activeUtilityTab, setActiveUtilityTab] = useState<string>("");
  const [measurementMethods, setMeasurementMethods] = useState<
    Record<string, "meter" | "quantity">
  >({});
  const [overrideQuantities, setOverrideQuantities] = useState<
    Record<string, boolean>
  >({});
  const [customQuantities, setCustomQuantities] = useState<
    Record<string, string | number>
  >({});
  const [activeConnectionTabs, setActiveConnectionTabs] = useState<
    Record<string, number>
  >({});
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingConnectionsList, setPendingConnectionsList] = useState<
    PendingConnection[]
  >([]);
  const updateStatus = useUpdateStatus(Number(consumerId));

  // Report functionality hooks
  const downloadReport = useDownloadReport();
  const viewReport = useDownloadReport();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentTab = searchParams.get("tab");

    if (currentTab && currentTab !== activeUtilityTab) {
      setActiveUtilityTab(currentTab);
    }
  }, [location.search]);

  // Form array setup
  const { control, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      services: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "services",
  });

  const watchedServices = watch("services");
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(
    null,
  );
  const [currentMeterSearchData, setCurrentMeterSearchData] =
    useState<any>(null);

  // Enhanced state for meter details
  const [selectedMeterDetails, setSelectedMeterDetails] = useState<
    Record<number, any>
  >({});
  const [loadingMeterDetails, setLoadingMeterDetails] = useState<
    Record<number, boolean>
  >({});

  // Dynamic utility tabs state
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [selectedMeters, setSelectedMeters] = useState<Record<string, any>>({});
  const [showTableMap, setShowTableMap] = useState<Record<string, boolean>>({});

  // Access acceptance data from navigation state
  const acceptanceData = location.state?.acceptanceData;

  // Initialize the update consumer service mutation
  const updateConsumerServiceMutation = useUpdateMeterInstallation();

  // Initialize the update consumer installation status mutation
  const updateConsumerInstallationStatusMutation =
    useUpdateConsumerInstallationStatus();

  const updateConsumerMutation = useUpdateConsumer();

  const updateQuantityConnectionMutation = useUpdateQuantityConnection();

  const installationCompletedMutation = useInstallationCompleted();

  // Fetch consumer details to get the required data for payload
  const {
    data: consumerDetailsData,
    isLoading: isLoadingConsumerDetails,
    refetch: refetchConsumerDetails,
  } = useConsumerDetails({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId,
    is_detail_info: true,
  });

  // Fetch connection detail
  const {
    data: connectionDetailData,
    isLoading: isLoadingConnectionDetail,
    refetch: refetchConnectionDetail,
  } = useConnectionDetail({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId || "",
    tab_name: "INSTALLATION_INPROGRESS",
  });

  // Fetch quantity-wise resource details
  const {
    data: quantityWiseResourceData,
    isLoading: isLoadingQuantityWiseResource,
    refetch: refetchQuantityWiseResource,
  } = useGetQuantityWiseResource({
    remoteUtilityId,
    consumerId,
  });

  const { data: utilityData } = useUtilityServices();

  const getUtilityOptions = useMemo(
    () =>
      utilityData
        ? utilityData
            .filter((utility) => utility.isActive === true)
            .map((utility) => ({
              value: utility.name,
              label: utility.name,
            }))
        : [],
    [utilityData],
  );
  // Get premise code from consumer details
  const premiseCode = useMemo(() => {
    return consumerDetailsData?.result?.addressData?.service?.PREMISE || "";
  }, [consumerDetailsData]);

  // Get meter mapping data from consumer details
  const meterMappingData = useMemo(() => {
    return consumerDetailsData?.result?.consumerMappingData || [];
  }, [consumerDetailsData]);

  // True once status is Installation Completed / Activation / Activated —
  // at that point action buttons should be disabled (data still visible).
  const isInstallationCompleted = useMemo(() => {
    const statusStr = (
      consumerDetailsData?.result?.statusDisplay ||
      consumerDetailsData?.result?.status_display ||
      application?.statusDisplay ||
      ""
    ).toUpperCase();
    return (
      statusStr.includes("INSTALLATION COMPLETED") ||
      statusStr.includes("ACTIVATION") ||
      statusStr.includes("COMPLETED")
    );
  }, [consumerDetailsData, application?.statusDisplay]);

  // Get utility services dynamically from quantity-wise resource API or consumer details tab-wise
  const utilityServices = useMemo(() => {
    const rawResult = quantityWiseResourceData?.result;
    let servicesList: string[] = [];

    if (rawResult && typeof rawResult === "object") {
      const keys = Object.keys(rawResult);
      if (keys.length > 0) {
        servicesList = keys.filter((key) => {
          const serviceData = rawResult[key];
          const isMeterReq =
            serviceData?.isMeterRequired ?? serviceData?.is_meter_required;
          return isMeterReq !== false;
        });
      }
    }

    if (servicesList.length === 0) {
      const rawServices = consumerDetailsData?.result?.utilityService || [];
      if (Array.isArray(rawServices)) {
        servicesList = rawServices;
      }
    }

    // Filter out services where isMeterRequired === false from utilityData configuration if available
    const allUtilityServices = utilityData?.result || utilityData || [];
    if (Array.isArray(allUtilityServices) && allUtilityServices.length > 0) {
      servicesList = servicesList.filter((service: string) => {
        const match = allUtilityServices.find(
          (s: any) => s.name?.toLowerCase() === service?.toLowerCase(),
        );
        if (match && (match.isMeterRequired === false || match.is_meter_required === false)) {
          return false;
        }
        return true;
      });
    }

    return servicesList;
  }, [quantityWiseResourceData, consumerDetailsData, utilityData]);

  useEffect(() => {
    if (utilityServices.length > 0) {
      if (!activeUtilityTab || !utilityServices.includes(activeUtilityTab)) {
        setActiveUtilityTab(utilityServices[0]);
      }
    }
  }, [utilityServices, activeUtilityTab]);

  // Connection count per service, taking the MAXIMUM found across every
  // available source (quantityWiseResourceData, connectionDetailData,
  // consumerDetailsData). Any one of these three APIs may be the one that
  // actually has the real number for a given test/consumer — trusting only
  // one and defaulting the rest to 1 is what caused getConnKey to collapse
  // multiple real connections (e.g. connections 2 and 3) onto the same
  // bare key as connection 1, silently corrupting all per-connection state.
  const utilityServiceQuantityMapping = useMemo(() => {
    const mapping: Record<string, number> = {};

    const registerCount = (serviceKeyRaw: string, count: number) => {
      if (!count || count < 1) return;
      const matched =
        utilityServices.find(
          (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
        ) || serviceKeyRaw;
      mapping[matched] = Math.max(mapping[matched] || 1, count);
    };

    // Source 1: quantityWiseResourceData (the "resource" API)
    const qwResult = quantityWiseResourceData?.result;
    if (qwResult && typeof qwResult === "object") {
      Object.entries(qwResult).forEach(([service, data]: [string, any]) => {
        const connInfo = data?.connectionInfo || data?.connection_info;
        const count =
          data?.numberOfConnection ??
          (connInfo && typeof connInfo === "object"
            ? Array.isArray(connInfo)
              ? connInfo.length
              : Object.keys(connInfo).length
            : 1);
        registerCount(service, Number(count) || 1);
      });
    }

    // Source 2: connectionDetailData — may report a service's connection
    // count even when the resource API doesn't have it populated (or vice
    // versa), and its own connectionInfo key count is itself a reliable
    // lower bound regardless of what numberOfConnection says.
    const cdResult = connectionDetailData?.result;
    if (cdResult && typeof cdResult === "object") {
      Object.entries(cdResult).forEach(([service, data]: [string, any]) => {
        if (!data || typeof data !== "object") return;
        const connInfo = data.connectionInfo || data.connection_info;
        const declaredCount =
          data.numberOfConnection ?? data.number_of_connection;
        const keyCount =
          connInfo && typeof connInfo === "object"
            ? Object.keys(connInfo).length
            : 0;
        const count = Math.max(Number(declaredCount) || 0, keyCount);
        if (count > 0) registerCount(service, count);
      });
    }

    // Source 3: consumerDetailsData's own recorded mapping, as a last resort.
    const cdsMapping =
      consumerDetailsData?.result?.additionalData
        ?.utilityServiceQuantityMapping;
    if (cdsMapping && typeof cdsMapping === "object") {
      Object.entries(cdsMapping).forEach(([service, count]: [string, any]) => {
        registerCount(service, Number(count) || 1);
      });
    }

    return mapping;
  }, [
    quantityWiseResourceData,
    connectionDetailData,
    consumerDetailsData,
    utilityServices,
  ]);

  // Default non-metered services dynamically to quantity-based measurement tab-wise
  useEffect(() => {
    utilityServices.forEach((service: string) => {
      const allUtilityServices = utilityData?.result || utilityData || [];
      const match = allUtilityServices.find(
        (s: any) => s.name?.toLowerCase() === service?.toLowerCase(),
      );
      if (match && match.isMeterRequired === false) {
        setMeasurementMethods((prev) => {
          if (prev[service] !== "quantity") {
            return { ...prev, [service]: "quantity" };
          }
          return prev;
        });
      }
    });
  }, [utilityServices, utilityData]);

  // Auto-select "Quantity based" and pre-fill the quantity value when
  // connectionDetailData carries a service-level quantity_based entry (no
  // connectionInfo at all — just { action, quantity, status } directly under
  // the service key). CamelCase only. The number of connections shown
  // ("Connections requested") always comes from utilityServiceQuantityMapping
  // (the resource API), never from this quantity value — they're different
  // numbers: quantity is the billed unit total, connections is the resource
  // count.
  useEffect(() => {
    const rawResult = connectionDetailData?.result;
    if (!rawResult || typeof rawResult !== "object") return;

    Object.entries(rawResult).forEach(
      ([serviceKeyRaw, serviceData]: [string, any]) => {
        if (!serviceData || typeof serviceData !== "object") return;
        if (serviceData.action !== "quantity_based") return;

        const matchedService =
          utilityServices.find(
            (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
          ) || serviceKeyRaw;

        setMeasurementMethods((prev) =>
          prev[matchedService] === "quantity"
            ? prev
            : { ...prev, [matchedService]: "quantity" },
        );

        if (
          serviceData.quantity !== undefined &&
          serviceData.quantity !== null
        ) {
          setOverrideQuantities((prev) => ({
            ...prev,
            [matchedService]: true,
          }));
          setCustomQuantities((prev) => ({
            ...prev,
            [matchedService]: serviceData.quantity,
          }));
        }

        setFrozenMeters((prev) => ({ ...prev, [matchedService]: true }));
      },
    );
  }, [connectionDetailData, utilityServices]);

  const getConnKey = (service: string, connNum?: number) => {
    const activeNum = connNum ?? (activeConnectionTabs[service] || 1);
    const connQty = utilityServiceQuantityMapping[service] || 1;
    if (connQty > 1) {
      return `${service}_conn_${activeNum}`;
    }
    return service;
  };

  const getConnValue = <T extends unknown>(
    state: Record<string, T>,
    service: string,
    connNum?: number,
  ): T | undefined => {
    const key = getConnKey(service, connNum);
    if (state[key] !== undefined) return state[key];
    const activeNum = connNum ?? (activeConnectionTabs[service] || 1);
    if (activeNum === 1 && state[service] !== undefined) return state[service];
    return undefined;
  };

  // Auto-select "No Resource Connected" per connection, sourced from
  // consumerDetailsData.result.additionalData.installationData (camelCase
  // only — no installation_data snake_case fallback). This is per-connection
  // granular data: { gas: { "1": { action: "no_meter_connected", ... } } }
  // means ONLY connection 1 of Gas is no-meter, not the whole service.
  // Object.entries + Number() naturally skips non-numeric keys (like the
  // "action"/"status"/"quantity" keys used by the quantity_based shape for
  // other services), so only real connection-number entries get applied.
  useEffect(() => {
    const installationData =
      consumerDetailsData?.result?.additionalData?.installationData;
    if (!installationData || typeof installationData !== "object") return;

    const updates: Record<string, "no-meter"> = {};

    Object.entries(installationData).forEach(
      ([serviceKeyRaw, serviceEntry]: [string, any]) => {
        if (!serviceEntry || typeof serviceEntry !== "object") return;

        const matchedService =
          utilityServices.find(
            (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
          ) || serviceKeyRaw;

        Object.entries(serviceEntry).forEach(
          ([connNumStr, connEntry]: [string, any]) => {
            if (!connEntry || typeof connEntry !== "object") return;
            if (connEntry.action !== "no_meter_connected") return;

            const connNum = Number(connNumStr);
            if (!Number.isFinite(connNum)) return;

            updates[getConnKey(matchedService, connNum)] = "no-meter";
          },
        );
      },
    );

    if (Object.keys(updates).length > 0) {
      setInstallationTypes((prev) => ({ ...prev, ...updates }));
    }
  }, [consumerDetailsData, utilityServices, utilityServiceQuantityMapping]);

  // Same as above, but sourced from connectionDetailData's connectionInfo
  // instead of consumerDetailsData — your API puts the no_meter_connected
  // marker in connectionInfo[connNum].action just as often as it puts
  // schedule/assign methods there, so this needs to be read from both
  // places rather than assuming one or the other.
  useEffect(() => {
    const rawResult = connectionDetailData?.result;
    if (!rawResult || typeof rawResult !== "object") return;

    const updates: Record<string, "no-meter"> = {};

    Object.entries(rawResult).forEach(
      ([serviceKeyRaw, serviceData]: [string, any]) => {
        if (!serviceData || typeof serviceData !== "object") return;

        const matchedService =
          utilityServices.find(
            (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
          ) || serviceKeyRaw;

        const connInfo =
          serviceData.connectionInfo || serviceData.connection_info;
        if (!connInfo || typeof connInfo !== "object") return;

        Object.entries(connInfo).forEach(
          ([connNumStr, connEntry]: [string, any]) => {
            if (!connEntry || typeof connEntry !== "object") return;
            if (connEntry.action !== "no_meter_connected") return;

            const connNum = Number(connNumStr);
            if (!Number.isFinite(connNum)) return;

            updates[getConnKey(matchedService, connNum)] = "no-meter";
          },
        );
      },
    );

    if (Object.keys(updates).length > 0) {
      setInstallationTypes((prev) => ({ ...prev, ...updates }));
    }
  }, [connectionDetailData, utilityServices, utilityServiceQuantityMapping]);

  // ---------------------------------------------------------------------
  // API-driven completion maps (camelCase only). These decide checkmarks
  // directly from connectionDetailData / quantityWiseResourceData so that
  // a connection/service that is already scheduled or assigned server-side
  // shows a check immediately on page load, without requiring the user to
  // re-touch it in the current session. Local session state (frozenMeters /
  // serviceOrderDetails) is still honored as a fallback for the moment right
  // after a user action, before the queries have refetched.
  // ---------------------------------------------------------------------

  // Per-connection method map, keyed by getConnKey(service, connNum).
  // Source of truth is ONLY connectionDetailData (server data), camelCase fields
  // only. The connection number is taken literally from the connectionInfo key
  // (e.g. gas's entry sits under key "1" => Connection 1, water's under key "2"
  // => Connection 2) — it is NOT derived from array index or local state.
  // This distinguishes "schedule_installation" from "assign_existing_meter" so
  // the Service Order Tracking panel never renders for a connection that was
  // actually assigned an existing meter (and vice versa).
  const connectionMethodMap = useMemo(() => {
    const map: Record<string, "schedule" | "assign" | undefined> = {};

    utilityServices.forEach((service: string) => {
      const connQty = utilityServiceQuantityMapping[service] || 1;

      // connectionDetailData keys utility services in lowercase ("gas", "water"),
      // so match case-insensitively back to the actual service name used in state.
      const cdServiceKey = Object.keys(connectionDetailData?.result || {}).find(
        (k) => k.toLowerCase() === service.toLowerCase(),
      );
      const cdConnInfo = cdServiceKey
        ? connectionDetailData.result[cdServiceKey]?.connectionInfo ||
          connectionDetailData.result[cdServiceKey]?.connection_info
        : undefined;

      for (let connNum = 1; connNum <= connQty; connNum++) {
        const key = getConnKey(service, connNum);
        const info = cdConnInfo?.[String(connNum)];

        if (info?.method === "schedule_installation") {
          map[key] = "schedule";
        } else if (info?.method === "assign_existing_meter") {
          map[key] = "assign";
        } else {
          map[key] = undefined;
        }
      }
    });

    return map;
  }, [utilityServices, utilityServiceQuantityMapping, connectionDetailData]);

  // Per-connection "done" map — used purely for the Connection tab checkmark.
  // True whenever connection_info has ANY data added for that literal
  // connection number key (e.g. "1" => Connection 1), regardless of what
  // shape/method that data takes. This is intentionally more lenient than
  // connectionMethodMap (which only recognizes the two known method values),
  // since the checkmark should reflect "data exists here", not "method
  // matched a known value".
  const connectionCompletionMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    utilityServices.forEach((service: string) => {
      const connQty = utilityServiceQuantityMapping[service] || 1;

      const cdServiceKey = Object.keys(connectionDetailData?.result || {}).find(
        (k) => k.toLowerCase() === service.toLowerCase(),
      );
      const cdConnInfo = cdServiceKey
        ? connectionDetailData.result[cdServiceKey]?.connectionInfo ||
          connectionDetailData.result[cdServiceKey]?.connection_info
        : undefined;

      for (let connNum = 1; connNum <= connQty; connNum++) {
        const key = getConnKey(service, connNum);
        const info = cdConnInfo?.[String(connNum)];
        map[key] = !!info && Object.keys(info).length > 0;
      }
    });

    return map;
  }, [utilityServices, utilityServiceQuantityMapping, connectionDetailData]);

  // Auto-populate the installation-type radio button from the API method,
  // per connection, so the UI reflects what the backend actually recorded
  // instead of defaulting back to the "schedule" radio.
  useEffect(() => {
    const updates: Record<string, "schedule" | "assign"> = {};
    Object.entries(connectionMethodMap).forEach(([key, method]) => {
      if (method === "schedule" || method === "assign") {
        updates[key] = method;
      }
    });
    if (Object.keys(updates).length > 0) {
      setInstallationTypes((prev) => ({ ...prev, ...updates }));
    }
  }, [connectionMethodMap]);

  // Service-level (parent tab) "done" map: true only once EVERY connection
  // for that service is individually done. Quantity-based services rely on
  // their own frozenMeters[service] flag (set by handleAssignQuantity) and
  // are left untouched here.
  const serviceCompletionMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    utilityServices.forEach((service: string) => {
      if (measurementMethods[service] === "quantity") {
        map[service] = !!frozenMeters[service];
        return;
      }

      const connQty = utilityServiceQuantityMapping[service] || 1;
      let allDone = true;
      for (let connNum = 1; connNum <= connQty; connNum++) {
        if (!connectionCompletionMap[getConnKey(service, connNum)]) {
          allDone = false;
          break;
        }
      }
      map[service] = allDone;
    });

    return map;
  }, [
    utilityServices,
    utilityServiceQuantityMapping,
    connectionCompletionMap,
    measurementMethods,
    frozenMeters,
  ]);

  const checkPendingConnections = () => {
    const pending: PendingConnection[] = [];

    utilityServices.forEach((service: string) => {
      const isQuantityMethod = measurementMethods[service] === "quantity";
      if (isQuantityMethod) return;

      const connQty = utilityServiceQuantityMapping[service] || 1;

      for (let cNum = 1; cNum <= connQty; cNum++) {
        const cKey = getConnKey(service, cNum);

        // Per-connection no-meter check (sourced from installationData via
        // the effect above, or from a same-session radio click) — only skips
        // THIS connection, not the whole service.
        const isNoMeter =
          installationTypes[cKey] === "no-meter" ||
          (cNum === 1 && installationTypes[service] === "no-meter");
        if (isNoMeter) continue;

        const isFrozen =
          frozenMeters[cKey] || (cNum === 1 && frozenMeters[service]);
        const hasSelectedMeter =
          !!selectedMeters[cKey] || (cNum === 1 && !!selectedMeters[service]);
        const hasSO =
          !!serviceOrderDetails[cKey] || !!serviceOrderDetails[service];
        const mapping = meterMappingData?.find(
          (m: any) => m.utilityService === service && m.meterDetails,
        );

        const isDone =
          isFrozen ||
          hasSO ||
          (cNum === 1 && !!mapping) ||
          connectionCompletionMap[cKey];
        if (!isDone) {
          pending.push({
            service,
            connNum: connQty > 1 ? cNum : undefined,
            totalConns: connQty,
            reason: hasSelectedMeter
              ? "Meter selected but not assigned"
              : "Pending meter assignment",
          });
        }
      }
    });

    return pending;
  };

  // Get only unassigned utility services (those without meterDetails)
  const unassignedUtilityServices = useMemo(() => {
    return utilityServices.filter((svc: string) => {
      const mapping = meterMappingData?.find(
        (m: any) => m.utilityService === svc,
      );
      return !mapping || !mapping.meterDetails;
    });
  }, [meterMappingData, utilityServices]);

  // Check if any utility service is missing meter details
  const hasUnassignedMeters = useMemo(() => {
    if (!utilityServices || utilityServices.length === 0) return false;
    return utilityServices.some((svc: string) => {
      const mapping = meterMappingData?.find(
        (m: any) => m.utilityService === svc,
      );
      return !mapping || !mapping.meterDetails;
    });
  }, [meterMappingData, utilityServices]);

  // Check if all services are ready for activation (either assigned or 'no-meter')
  const isReadyForActivation = useMemo(() => {
    if (!utilityServices || utilityServices.length === 0) return false;
    return utilityServices.every((svc: string) => {
      if (measurementMethods[svc] === "quantity") return true;
      const mapping = meterMappingData?.find(
        (m: any) => m.utilityService === svc,
      );
      if (mapping?.meterDetails) return true;
      if (selectedMeters[svc] && latestReadings[svc] && readingDates[svc])
        return true;
      if (installationTypes[svc] === "no-meter") return true;
      return false;
    });
  }, [
    meterMappingData,
    utilityServices,
    measurementMethods,
    installationTypes,
    selectedMeters,
    latestReadings,
    readingDates,
  ]);

  // Fetch service orders using the hook
  const {
    data: serviceOrdersResponse,
    isLoading: isLoadingServiceOrders,
    error: serviceOrdersError,
  } = useServiceOrder({
    remote_utility_id: remoteUtilityId,
    search_data: searchTerm,
    utility_service: activeUtilityTab || utilityServices[0],
  });

  const { data: priorityData } = useJobCardPriority();

  // Service order data for the currently active tab/connection, sourced
  // entirely from serviceOrderDetails (which is populated per-connection
  // from connectionDetailData — see the parsing useEffect below). This
  // replaces the old useServiceOrderConsumer global query.
  const activeConnectionServiceOrder = useMemo(() => {
    const svc = activeUtilityTab || utilityServices[0];
    if (!svc) return undefined;
    const connNum = activeConnectionTabs[svc] || 1;
    const key = getConnKey(svc, connNum);
    return serviceOrderDetails[key] || serviceOrderDetails[svc];
  }, [
    activeUtilityTab,
    utilityServices,
    activeConnectionTabs,
    utilityServiceQuantityMapping,
    serviceOrderDetails,
  ]);

  // Extract report ID for report functionality — scoped to the active connection.
  const reportId =
    activeConnectionServiceOrder?.id || activeConnectionServiceOrder?.jobCardNo;

  // Initialize form array when consumerDetailsData is available
  useEffect(() => {
    if (consumerDetailsData?.result?.consumerMappingData) {
      const initialServices =
        consumerDetailsData.result.consumerMappingData.map((product: any) => ({
          utilityService: product.utilityService,
          deviceNumber: "",
          latestReading: "",
          readingDate: "",
          selectedConsumer: null,
          selectedMeter: null,
        }));

      // Clear existing and append new services
      remove();
      initialServices.forEach((service: UtilityServiceForm) => append(service));
    }
  }, [consumerDetailsData, append, remove]);

  // Pre-populate connection-wise existing meters from quantityWiseResourceData (API response) using camelCase
  useEffect(() => {
    const rawResult = quantityWiseResourceData?.result;
    if (!rawResult || typeof rawResult !== "object") return;

    Object.entries(rawResult).forEach(
      ([service, serviceData]: [string, any]) => {
        const connInfo =
          serviceData?.connectionInfo || serviceData?.connection_info;
        if (!connInfo || typeof connInfo !== "object") return;

        const connEntries = Array.isArray(connInfo)
          ? connInfo.map((item: any, idx: number) => [String(idx + 1), item])
          : Object.entries(connInfo);

        connEntries.forEach(([connKeyStr, info]: [string, any]) => {
          if (
            !info ||
            typeof info !== "object" ||
            Object.keys(info).length === 0
          )
            return;

          const connNum = Number(connKeyStr) || 1;
          const cKey = getConnKey(service, connNum);

          const resourceId =
            info.resourceId ||
            info.resource_id ||
            info.meterId ||
            info.meter_id;
          const serialNo = info.serialNo || info.serial_no;
          const meterStatus = info.meterStatus || info.meter_status || "N/A";
          const physicalStatus =
            info.physicalStatus || info.physical_status || "N/A";
          const lastReading =
            info.lastReading !== undefined
              ? info.lastReading
              : info.last_reading;
          const rawDate =
            info.readDate ||
            info.read_date ||
            info.installDate ||
            info.install_date;

          if (resourceId || serialNo) {
            const meterObj = {
              id: info.meterId || info.meter_id || resourceId,
              meterId: info.meterId || info.meter_id || resourceId,
              meterNumber:
                resourceId ||
                serialNo ||
                String(info.meterId || info.meter_id || ""),
              meterNo:
                resourceId ||
                serialNo ||
                String(info.meterId || info.meter_id || ""),
              deviceNo: serialNo || resourceId || "",
              deviceNumber: serialNo || resourceId || "",
              currentReading: lastReading ?? 0,
              currentReadingDate: rawDate,
              status: meterStatus,
              statusDisplay: meterStatus,
              physicalStatus,
              physicalStatusDisplay: physicalStatus,
            };

            setSelectedMeters((prev) => ({
              ...prev,
              [cKey]: prev[cKey] || meterObj,
              ...(connNum === 1
                ? { [service]: prev[service] || meterObj }
                : {}),
            }));

            if (lastReading !== undefined && lastReading !== null) {
              setLatestReadings((prev) => ({
                ...prev,
                [cKey]: prev[cKey] || String(lastReading),
                ...(connNum === 1
                  ? { [service]: prev[service] || String(lastReading) }
                  : {}),
              }));
            }

            if (rawDate) {
              try {
                const normalized = normalizeDate(rawDate);
                if (normalized) {
                  setReadingDates((prev) => ({
                    ...prev,
                    [cKey]: prev[cKey] || new Date(normalized),
                    ...(connNum === 1
                      ? { [service]: prev[service] || new Date(normalized) }
                      : {}),
                  }));
                }
              } catch (e) {}
            }
          }
        });
      },
    );
  }, [quantityWiseResourceData, utilityServiceQuantityMapping]);

  // Pre-populate connection-wise service orders / assigned resources from the
  // Connection Detail API (useConnectionDetail -> connectionDetailData).
  // The connection number is taken directly from the key inside `connection_info`
  // (e.g. water's resource sits under key "2" => Connection 2), NOT from array index,
  // so it always lines up with the actual connection tab. Everything is normalized to
  // camelCase and nothing here freezes/locks a field — the user can still edit any
  // value up until they click "Continue to activation".
  useEffect(() => {
    const rawResult = connectionDetailData?.result;
    if (!rawResult || typeof rawResult !== "object") return;

    Object.entries(rawResult).forEach(
      ([serviceKeyRaw, serviceData]: [string, any]) => {
        if (!serviceData || typeof serviceData !== "object") return;

        // The API may return the utility service in lowercase ("gas", "water"),
        // match it back to the actual utility service name used across state ("Gas", "Water").
        const matchedService =
          utilityServices.find(
            (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
          ) || serviceKeyRaw;

        const connInfo =
          serviceData.connectionInfo || serviceData.connection_info;
        if (!connInfo || typeof connInfo !== "object") return;

        Object.entries(connInfo).forEach(
          ([connNumStr, info]: [string, any]) => {
            if (
              !info ||
              typeof info !== "object" ||
              Object.keys(info).length === 0
            )
              return;

            // Connection number comes straight from the connectionInfo key.
            const connNum = Number(connNumStr) || 1;
            const cKey = getConnKey(matchedService, connNum);

            const method = info.method;
            const isScheduled =
              method === "schedule_installation" ||
              !!info.serviceOrderId ||
              !!info.service_order_id;
            const isAssigned =
              method === "assign_existing_meter" ||
              !!info.meterId ||
              !!info.meter_id ||
              !!info.meter;

            if (isScheduled) {
              const so = info.serviceOrder || info.service_order || {};
              const sopData = so.sop || {};
              const territory = so.territoryData || so.territory_data || {};
              const fieldForceRaw = so.fieldForce || so.field_force;

              const serviceOrderObj = {
                id: so.id ?? info.serviceOrderId ?? info.service_order_id,
                sopId: info.sopId ?? info.sop_id,
                name: sopData.name || "",
                status: so.status,
                statusDisplay:
                  so.statusDisplay ||
                  so.status_display ||
                  info.status ||
                  "Scheduled",
                sopType: sopData.sopType || sopData.sop_type,
                sopSubtype: sopData.sopSubtype || sopData.sop_subtype,
                sopTypeDisplay: so.sopTypeDisplay || so.sop_type_display,
                sopSubtypeDisplay:
                  so.sopSubtypeDisplay || so.sop_subtype_display,
                jobCardNo: so.jobCardNo || so.job_card_no,
                priority: so.priority,
                priorityDisplay: so.priorityDisplay || so.priority_display,
                createdDate: so.createdDate || so.created_date,
                assignDate: so.assignDate || so.assign_date,
                expectedEndDate: so.expectedEndDate || so.expected_end_date,
                requestAvailabilityDate:
                  so.requestAvailabilityDate || so.request_availability_date,
                requestAccountNo: so.requestAccountNo || so.request_account_no,
                requestUsername: so.requestUsername || so.request_username,
                mobileNumber: so.mobileNumber || so.mobile_number,
                fieldForce: fieldForceRaw
                  ? {
                      name: fieldForceRaw.name || fieldForceRaw,
                    }
                  : undefined,
                territoryData: {
                  area: territory.area,
                  subArea: territory.subArea || territory.sub_area,
                  premise: territory.premise,
                },
                remark: so.remark,
                connectionNumber: connNum,
              };

              setServiceOrderDetails((prev) => ({
                ...prev,
                [cKey]: serviceOrderObj,
                ...(connNum === 1 ? { [matchedService]: serviceOrderObj } : {}),
              }));

              setShowServiceOrderDetails((prev) => ({
                ...prev,
                [matchedService]: true,
              }));
            }

            if (isAssigned) {
              const meterRaw = info.meter || {};

              const meterObj = {
                id: info.meterId ?? info.meter_id ?? meterRaw.id,
                meterId: info.meterId ?? info.meter_id ?? meterRaw.id,
                meterNo:
                  info.meterNo ||
                  info.meter_no ||
                  meterRaw.meterNumber ||
                  meterRaw.meter_number ||
                  "",
                meterNumber:
                  info.meterNo ||
                  info.meter_no ||
                  meterRaw.meterNumber ||
                  meterRaw.meter_number ||
                  "",
                deviceNo:
                  info.deviceNo ||
                  info.device_no ||
                  meterRaw.deviceNo ||
                  meterRaw.device_no ||
                  "",
                deviceNumber:
                  info.deviceNo ||
                  info.device_no ||
                  meterRaw.deviceNo ||
                  meterRaw.device_no ||
                  "",
                currentReading:
                  info.currentReading ?? info.current_reading ?? 0,
                currentReadingDate:
                  info.currentReadingDate || info.current_reading_date,
                status:
                  meterRaw.statusDisplay ||
                  meterRaw.status_display ||
                  info.status ||
                  "Assigned",
                statusDisplay:
                  meterRaw.statusDisplay ||
                  meterRaw.status_display ||
                  info.status ||
                  "Assigned",
                physicalStatus:
                  meterRaw.physicalStatusDisplay ||
                  meterRaw.physical_status_display ||
                  "Installed",
                physicalStatusDisplay:
                  meterRaw.physicalStatusDisplay ||
                  meterRaw.physical_status_display ||
                  "Installed",
              };

              setSelectedMeters((prev) => ({
                ...prev,
                [cKey]: meterObj,
                ...(connNum === 1 ? { [matchedService]: meterObj } : {}),
              }));

              const readingVal = info.currentReading ?? info.current_reading;
              if (readingVal !== undefined && readingVal !== null) {
                setLatestReadings((prev) => ({
                  ...prev,
                  [cKey]: String(readingVal),
                  ...(connNum === 1
                    ? { [matchedService]: String(readingVal) }
                    : {}),
                }));
              }

              const rawDate =
                info.currentReadingDate || info.current_reading_date;
              if (rawDate) {
                try {
                  const normalized = normalizeDate(rawDate);
                  if (normalized) {
                    setReadingDates((prev) => ({
                      ...prev,
                      [cKey]: new Date(normalized),
                      ...(connNum === 1
                        ? { [matchedService]: new Date(normalized) }
                        : {}),
                    }));
                  }
                } catch (e) {
                  console.error(
                    "Invalid date from connection detail:",
                    rawDate,
                  );
                }
              }
            }
          },
        );
      },
    );
  }, [connectionDetailData, utilityServices]);

  // Initialize display data from backend data when component mounts.
  // NOTE: this intentionally does NOT freeze/lock any field — per requirement,
  // the user must be able to edit everything until they click "Continue to activation".
  useEffect(() => {
    if (consumerDetailsData?.result) {
      const additionalData =
        consumerDetailsData.result.additionalData ||
        consumerDetailsData.result.additional_data;

      const extraMeterData =
        additionalData?.installationMeterData?.utilityServiceMeterData ||
        additionalData?.installation_meter_data?.utility_service_meter_data;
      const installationData =
        additionalData?.installationData || additionalData?.installation_data;

      utilityServices.forEach((service) => {
        const serviceKey = service.toLowerCase();
        const mapping = meterMappingData?.find(
          (meter: any) =>
            meter.utilityService === service && meter.meterDetails,
        );

        const meterEntries =
          extraMeterData?.[serviceKey] || extraMeterData?.[service];

        if (Array.isArray(meterEntries)) {
          meterEntries.forEach((entry: any) => {
            if (!entry || typeof entry !== "object") return;
            const connNum =
              entry.connectionNumber ?? entry.connection_number ?? 1;
            const cKey = getConnKey(service, connNum);
            const readingVal = entry.currentReading ?? entry.current_reading;
            const rawDate =
              entry.currentReadingDate ?? entry.current_reading_date;

            if (readingVal !== undefined && readingVal !== null) {
              setLatestReadings((prev) => ({
                ...prev,
                [cKey]: String(readingVal),
                ...(connNum === 1 ? { [service]: String(readingVal) } : {}),
              }));
            }

            if (rawDate) {
              try {
                const normalized = normalizeDate(rawDate);
                if (normalized) {
                  setReadingDates((prev) => ({
                    ...prev,
                    [cKey]: new Date(normalized),
                    ...(connNum === 1
                      ? { [service]: new Date(normalized) }
                      : {}),
                  }));
                }
              } catch (e) {
                console.error("Invalid date:", rawDate);
              }
            }

            const mId = entry.meterId ?? entry.meter_id;
            const mNo = entry.meterNo ?? entry.meter_no;
            const dNo = entry.deviceNo ?? entry.device_no;

            if (mId || mNo) {
              const meterObj = {
                id: mId,
                meterId: mId,
                meterNo: mNo,
                deviceNo: dNo,
              };

              setSelectedMeters((prev) => ({
                ...prev,
                [cKey]: meterObj,
                ...(connNum === 1 ? { [service]: meterObj } : {}),
              }));
            }
          });
        } else if (meterEntries && typeof meterEntries === "object") {
          const entry = meterEntries;
          const connNum =
            entry.connectionNumber ?? entry.connection_number ?? 1;
          const cKey = getConnKey(service, connNum);
          const readingVal = entry.currentReading ?? entry.current_reading;
          const rawDate =
            entry.currentReadingDate ?? entry.current_reading_date;

          if (readingVal !== undefined && readingVal !== null) {
            setLatestReadings((prev) => ({
              ...prev,
              [cKey]: String(readingVal),
              [service]: String(readingVal),
            }));
          }

          if (rawDate) {
            try {
              const normalized = normalizeDate(rawDate);
              if (normalized) {
                setReadingDates((prev) => ({
                  ...prev,
                  [cKey]: new Date(normalized),
                  [service]: new Date(normalized),
                }));
              }
            } catch (e) {}
          }

          const mId = entry.meterId ?? entry.meter_id;
          const mNo = entry.meterNo ?? entry.meter_no;
          const dNo = entry.deviceNo ?? entry.device_no;

          if (mId || mNo) {
            const meterObj = {
              id: mId,
              meterId: mId,
              meterNo: mNo,
              deviceNo: dNo,
            };

            setSelectedMeters((prev) => ({
              ...prev,
              [cKey]: meterObj,
              [service]: meterObj,
            }));
          }
        }

        // Also check installationData per connection
        const serviceInstallation =
          installationData?.[serviceKey] || installationData?.[service];
        if (serviceInstallation && typeof serviceInstallation === "object") {
          Object.entries(serviceInstallation).forEach(
            ([connNumStr, entry]: [string, any]) => {
              if (!entry || typeof entry !== "object") return;
              const connNum = Number(connNumStr) || 1;
              const cKey = getConnKey(service, connNum);
              const readingVal = entry.currentReading ?? entry.current_reading;
              const rawDate =
                entry.currentReadingDate ?? entry.current_reading_date;

              if (readingVal !== undefined && readingVal !== null) {
                setLatestReadings((prev) => ({
                  ...prev,
                  [cKey]: prev[cKey] || String(readingVal),
                  ...(connNum === 1
                    ? { [service]: prev[service] || String(readingVal) }
                    : {}),
                }));
              }

              if (rawDate) {
                try {
                  const normalized = normalizeDate(rawDate);
                  if (normalized) {
                    setReadingDates((prev) => ({
                      ...prev,
                      [cKey]: prev[cKey] || new Date(normalized),
                      ...(connNum === 1
                        ? { [service]: prev[service] || new Date(normalized) }
                        : {}),
                    }));
                  }
                } catch (e) {}
              }

              const mId = entry.meterId ?? entry.meter_id;
              const mNo = entry.meterNo ?? entry.meter_no;
              const dNo = entry.deviceNo ?? entry.device_no;

              if (mId || mNo) {
                const meterObj = {
                  id: mId,
                  meterId: mId,
                  meterNo: mNo,
                  deviceNo: dNo,
                };

                setSelectedMeters((prev) => ({
                  ...prev,
                  [cKey]: prev[cKey] || meterObj,
                  ...(connNum === 1
                    ? { [service]: prev[service] || meterObj }
                    : {}),
                }));
              }
            },
          );
        }

        if (mapping?.meterDetails) {
          const md = mapping.meterDetails;
          const readingVal = md.currentReading ?? md.current_reading;
          if (readingVal !== undefined && readingVal !== null) {
            setLatestReadings((prev) => ({
              ...prev,
              [service]: prev[service] || String(readingVal),
            }));
          }

          const rawDate = md.currentReadingDate ?? md.current_reading_date;
          if (rawDate) {
            try {
              const normalized = normalizeDate(rawDate);
              if (normalized) {
                setReadingDates((prev) => ({
                  ...prev,
                  [service]: prev[service] || new Date(normalized),
                }));
              }
            } catch (error) {}
          }

          const mId = md.id ?? md.meterId ?? md.meter_id;
          const mNo = md.meterNo ?? md.meter_no;
          const dNo = md.deviceNo ?? md.device_no;

          if (mId || mNo) {
            setSelectedMeters((prev) => ({
              ...prev,
              [service]: prev[service] || {
                id: mId,
                meterId: mId,
                meterNo: mNo,
                deviceNo: dNo,
              },
            }));
          }
        }
      });
    }
  }, [consumerDetailsData, meterMappingData, utilityServices]);

  // Auto-advance the workflow once the service order is Approved.
  // Sourced entirely from serviceOrderDetails (connectionDetailData-derived)
  // now that the global useServiceOrderConsumer query has been removed.
  useEffect(() => {
    if (activeConnectionServiceOrder?.statusDisplay === "Approved") {
      updateApplicationStatus("activation");
      moveToNextStep();
    }
  }, [activeConnectionServiceOrder?.statusDisplay]);

  // Initialize search queries when utility services are loaded
  useEffect(() => {
    if (utilityServices.length > 0) {
      const initialQueries: Record<string, string> = {};
      utilityServices.forEach((service: string) => {
        initialQueries[service] = "";
      });
      setSearchQueries(initialQueries);
    }
  }, [utilityServices]);

  const effectiveService =
    activeUtilityTab ||
    (utilityServices && utilityServices.length > 0 ? utilityServices[0] : "");

  const params = useMemo(() => {
    return {
      consumer_id: consumerId,
      remote_utility_id: remoteUtilityId,
      utility_service: effectiveService,
    };
  }, [consumerId, remoteUtilityId, effectiveService]);

  const { data: meterSearchData } = useAssignedUnitResources(params);
  console.warn("meter api params", params);
  // Enhanced function to fetch meter details after selection
  const fetchMeterDetails = async (
    deviceNumber: string,
    utilityService: string,
    index: number,
  ) => {
    try {
      setLoadingMeterDetails((prev) => ({ ...prev, [index]: true }));
      if (currentMeterSearchData?.result) {
        const selectedMeterData = currentMeterSearchData.result.find(
          (meter: any) => meter.deviceNo === deviceNumber,
        );

        if (selectedMeterData) {
          // Update the selected meter details state
          setSelectedMeterDetails((prev) => ({
            ...prev,
            [index]: selectedMeterData,
          }));

          // Update form field with selected meter data
          setValue(`services.${index}.selectedMeter`, selectedMeterData);

          toast({
            title: "Meter Details Loaded",
            description: `Meter ID: ${selectedMeterData.id} for device ${deviceNumber}`,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching meter details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch meter details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingMeterDetails((prev) => ({ ...prev, [index]: false }));
    }
  };

  // Handle device number search for form array
  const handleDeviceNumberChange = async (value: string, index: number) => {
    setValue(`services.${index}.deviceNumber`, value);
    setCurrentSearchValue(value);

    // Set the active utility service for the current index
    const currentService =
      watchedServices[index]?.utilityService || fields[index]?.utilityService;
    setActiveUtilityService(currentService);

    if (value.length >= 3) {
      setActiveSearchIndex(index);
      setCurrentMeterSearchData(meterSearchData);
    } else {
      setActiveSearchIndex(null);
      setCurrentMeterSearchData(null);
      // Clear selected meter details when search is cleared
      setSelectedMeterDetails((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      setValue(`services.${index}.selectedMeter`, null);
    }
  };

  // Enhanced consumer selection handler
  const handleConsumerSelect = async (meter: any, index: number) => {
    const deviceNumber = meter.deviceNo || "";
    const utilityService =
      watchedServices[index]?.utilityService || fields[index]?.utilityService;

    setValue(`services.${index}.deviceNumber`, deviceNumber);
    setValue(`services.${index}.selectedConsumer`, meter);

    // Clear search UI states but keep the device number and utility service for API call
    setActiveSearchIndex(null);
    setCurrentMeterSearchData(null);

    // Fetch detailed meter information after selection with the selected device number and utility service
    if (deviceNumber && utilityService) {
      await fetchMeterDetails(deviceNumber, utilityService, index);
    }

    // Clear search states after API call
    setCurrentSearchValue("");
    setActiveUtilityService("");
  };

  // Report functionality handlers
  const handleViewReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await viewReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      toast({
        title: "Download started",
        description: "The inspection report is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the inspection report.",
        variant: "destructive",
      });
    }
  };

  // Create multiple useAssignedUnitResources hooks for each utility service
  const meterQueries = useMemo(() => {
    return utilityServices.reduce((acc: any, service: string) => {
      acc[service] = {
        consumer_id: consumerId,
        remote_utility_id: remoteUtilityId,
        utility_service: service,
      };
      return acc;
    }, {});
  }, [utilityServices, consumerId, remoteUtilityId]);

  // Use conditional hooks for each utility service
  const waterMetersQuery = useAssignedUnitResources(
    utilityServices.includes("Water") ? meterQueries["Water"] : null,
  );

  const wasteWaterMetersQuery = useAssignedUnitResources(
    utilityServices.includes("Waste Water")
      ? meterQueries["Waste Water"]
      : null,
  );

  const electricMetersQuery = useAssignedUnitResources(
    utilityServices.includes("Electricity")
      ? meterQueries["Electricity"]
      : null,
  );

  const gasMetersQuery = useAssignedUnitResources(
    utilityServices.includes("Gas") ? meterQueries["Gas"] : null,
  );

  const activeTabMeterQuery = useAssignedUnitResources(
    effectiveService
      ? {
          consumer_id: consumerId,
          remote_utility_id: remoteUtilityId,
          utility_service: effectiveService,
        }
      : null,
  );

  // Create a mapping of utility services to their respective query results
  const meterQueriesResults = useMemo(() => {
    const results: Record<string, any> = {};
    if (utilityServices.includes("Water")) {
      results["Water"] = waterMetersQuery;
    }
    if (utilityServices.includes("Waste Water")) {
      results["Waste Water"] = wasteWaterMetersQuery;
    }
    if (utilityServices.includes("Electricity")) {
      results["Electricity"] = electricMetersQuery;
    }
    if (utilityServices.includes("Gas")) {
      results["Gas"] = gasMetersQuery;
    }
    if (effectiveService && !results[effectiveService]) {
      results[effectiveService] = activeTabMeterQuery;
    }
    return results;
  }, [
    utilityServices,
    waterMetersQuery,
    wasteWaterMetersQuery,
    electricMetersQuery,
    gasMetersQuery,
    effectiveService,
    activeTabMeterQuery,
  ]);
  const priorityOptions =
    priorityData?.map((priority) => ({
      value: priority.key,
      label: priority.value,
    })) || [];
  // Transform API response to the format expected by the form
  const serviceOrderOptions = useMemo(() => {
    if (!serviceOrdersResponse?.result) {
      return [];
    }

    const options = serviceOrdersResponse.result.map((order: any) => ({
      label: ` ${order.name}`,
      value: order.id.toString(),
      data: {
        id: order.id,
        service_no: order.service_no,
        name: order.name,
        sop_type: order.sop_type,
        sop_subtype: order.sop_subtype,
        short_name: order.short_name,
        est_total_cost: order.est_total_cost,
        est_effort: order.est_effort,
      },
    }));

    return options;
  }, [serviceOrdersResponse]);

  // Helper to normalize resource/meter object converting snake_case to camelCase
  const normalizeMeterResource = (item: any) => {
    if (!item || typeof item !== "object") return item;

    const resourceId =
      item.resourceId ??
      item.resource_id ??
      item.meterNo ??
      item.meterNumber ??
      item.meter_no ??
      (item.meter_id ? String(item.meter_id) : undefined);

    const meterId = item.meterId ?? item.meter_id ?? item.id;

    const serialNo =
      item.serialNo ??
      item.serial_no ??
      item.deviceNo ??
      item.deviceNumber ??
      item.device_no;

    const physicalStatus =
      item.physicalStatus ??
      item.physical_status ??
      item.physicalStatusDisplay ??
      "Installed";

    const meterStatus =
      item.meterStatus ??
      item.meter_status ??
      item.statusDisplay ??
      item.status ??
      "Assigned";

    const lastReading =
      item.lastReading ??
      item.last_reading ??
      item.latestReading ??
      item.latest_reading ??
      item.currentReading ??
      item.current_reading ??
      0;

    const readDate =
      item.readDate ??
      item.read_date ??
      item.readingDate ??
      item.reading_date ??
      item.installDate ??
      item.install_date;

    return {
      ...item,
      id: item.id ?? meterId ?? resourceId,
      resourceId,
      resource_id: resourceId,
      meterId,
      meter_id: meterId,
      meterNo: resourceId,
      meterNumber: resourceId,
      meter_no: resourceId,
      serialNo,
      serial_no: serialNo,
      deviceNo: serialNo,
      deviceNumber: serialNo,
      device_no: serialNo,
      physicalStatus,
      physical_status: physicalStatus,
      physicalStatusDisplay: physicalStatus,
      meterStatus,
      meter_status: meterStatus,
      status: meterStatus,
      statusDisplay: meterStatus,
      lastReading,
      last_reading: lastReading,
      latestReading: String(lastReading),
      readDate,
      read_date: readDate,
      readingDate: readDate,
    };
  };

  // Filter meters based on search query for active tab
  const getFilteredMeters = (utilityService: string) => {
    const queryResult = meterQueriesResults[utilityService];
    if (!queryResult?.data?.result) return [];

    const searchQuery = searchQueries[utilityService] || "";
    const rawData = queryResult.data.result;
    const rawList = Array.isArray(rawData) ? rawData : [rawData];

    return rawList.map(normalizeMeterResource).filter((meter: any) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        (meter.resourceId || meter.meterNo || "")?.toLowerCase().includes(q) ||
        (meter.serialNo || meter.deviceNo || "")?.toLowerCase().includes(q)
      );
    });
  };

  // Handle search query change for specific utility
  const handleSearchQueryChange = (utilityService: string, query: string) => {
    setSearchQueries((prev) => ({
      ...prev,
      [utilityService]: query,
    }));
  };

  // Enhanced meter selection for tabs approach
  const handleMeterSelect = async (utilityService: string, rawMeter: any) => {
    const meter = normalizeMeterResource(rawMeter);
    const connKey = getConnKey(utilityService);
    const meterObj = {
      ...meter,
      meterId: meter.meterId || meter.id,
      resourceId: meter.resourceId,
    };

    setSelectedMeters((prev) => ({
      ...prev,
      [connKey]: meterObj,
      [utilityService]: meterObj,
    }));

    setShowTableMap((prev) => ({
      ...prev,
      [connKey]: false,
      [utilityService]: false,
    }));

    // Auto-populate initial reading value from selected meter item immediately
    const initialReadingVal =
      meter.lastReading ??
      meter.last_reading ??
      meter.latestReading ??
      meter.latest_reading ??
      meter.currentReading ??
      meter.current_reading ??
      meter.initialReading ??
      meter.initial_reading ??
      meter.reading ??
      "0";

    setLatestReadings((prev) => ({
      ...prev,
      [connKey]: String(initialReadingVal),
      [utilityService]: String(initialReadingVal),
    }));

    // Auto-populate initial reading date from selected meter item immediately
    const initialDateRaw =
      meter.readDate ||
      meter.read_date ||
      meter.readingDate ||
      meter.reading_date ||
      meter.latestReadingDate ||
      meter.latest_reading_date ||
      meter.currentReadingDate ||
      meter.current_reading_date ||
      meter.installDate ||
      meter.install_date ||
      meter.createdDate ||
      meter.created_date;

    try {
      const normDate = initialDateRaw ? normalizeDate(initialDateRaw) : null;
      const initialDateObj = normDate ? new Date(normDate) : new Date();
      setReadingDates((prev) => ({
        ...prev,
        [connKey]: initialDateObj,
        [utilityService]: initialDateObj,
      }));
    } catch {
      setReadingDates((prev) => ({
        ...prev,
        [connKey]: new Date(),
        [utilityService]: new Date(),
      }));
    }

    // Fetch detailed meter information for tabs approach
    if (meter.deviceNo && utilityService) {
      try {
        const detailParams: any = {
          consumer_id: consumerId,
          remote_utility_id: String(remoteUtilityId),
          utility_service: utilityService,
        };

        const response =
          await CXCxOnboardingApi.getAssignedUnitResources(detailParams);

        if (response?.result && response.result.length > 0) {
          const selectedMeterData =
            response.result.find(
              (m: any) =>
                m.id === meter.id ||
                m.meterNo === meter.meterNo ||
                m.meterNumber === meter.meterNumber ||
                m.deviceNo === meter.deviceNo,
            ) || response.result[0];

          const fullMeterObj = {
            ...meter,
            detailedInfo: selectedMeterData,
            meterId: selectedMeterData.id || meter.id,
          };

          // Update selected meters with detailed information
          setSelectedMeters((prev) => ({
            ...prev,
            [connKey]: fullMeterObj,
            [utilityService]: fullMeterObj,
          }));

          const currentReading =
            selectedMeterData.currentReading ??
            selectedMeterData.current_reading ??
            selectedMeterData.latestReading ??
            selectedMeterData.latest_reading ??
            selectedMeterData.initialReading ??
            selectedMeterData.reading;

          if (currentReading !== undefined && currentReading !== null) {
            setLatestReadings((prev) => ({
              ...prev,
              [connKey]: String(currentReading),
              [utilityService]: String(currentReading),
            }));
          }

          const currentReadingDate =
            selectedMeterData.currentReadingDate ||
            selectedMeterData.current_reading_date ||
            selectedMeterData.latestReadingDate ||
            selectedMeterData.latest_reading_date ||
            selectedMeterData.readingDate ||
            selectedMeterData.createdDate;

          if (currentReadingDate) {
            try {
              const normalized = normalizeDate(currentReadingDate);
              if (normalized) {
                setReadingDates((prev) => ({
                  ...prev,
                  [connKey]: new Date(normalized),
                  [utilityService]: new Date(normalized),
                }));
              }
            } catch (error) {
              console.error(
                "Invalid date format from API:",
                currentReadingDate,
              );
            }
          }

          toast({
            title: "Meter Selected",
            description: `${utilityService} meter selected - ID: ${selectedMeterData.id || meter.id}`,
          });
        }
      } catch (error) {
        console.error("Error fetching meter details:", error);
      }
    }
  };

  // Check if at least one meter is selected
  const isAtLeastOneMeterSelected = () => {
    return Object.keys(selectedMeters).length > 0;
  };

  // Custom tabs configuration for utility services
  const utilityTabsConfig = useMemo(() => {
    const config: Record<string, any> = {};

    utilityServices.forEach((service: string) => {
      const queryResult = meterQueriesResults[service];
      const filteredMeters = getFilteredMeters(service);
      const isLoading = queryResult?.isLoading;
      const hasError = queryResult?.error;

      const serviceKey = service.toLowerCase();
      const mapping = meterMappingData?.find(
        (m: any) =>
          (m.utilityService === service ||
            m.utility_service === service ||
            m.utilityService?.toLowerCase() === serviceKey) &&
          m.meterDetails,
      );
      const additionalMeterData =
        consumerDetailsData?.result?.additionalData?.installationMeterData
          ?.utilityServiceMeterData?.[serviceKey] ||
        consumerDetailsData?.result?.additional_data?.installation_meter_data
          ?.utility_service_meter_data?.[serviceKey];

      const rawConnMeter =
        getConnValue(selectedMeters, service) ||
        (mapping?.meterDetails
          ? normalizeMeterResource(mapping.meterDetails)
          : null) ||
        (additionalMeterData
          ? normalizeMeterResource(additionalMeterData)
          : null);

      const connMeter = rawConnMeter
        ? normalizeMeterResource(rawConnMeter)
        : null;
      const isConnFrozen = getConnValue(frozenMeters, service);
      // Table opens automatically when no resource is selected yet, or when
      // the user explicitly clicks "Change Resource" on an existing one.
      const isTableOpen = !connMeter || !!getConnValue(showTableMap, service);

      config[service] = {
        label: `${service === "Waste Water" ? "Waste Water" : service} Utility`,
        icon:
          service === "Water" ? (
            <Package className="h-4 w-4" />
          ) : service === "Electricity" ? (
            <Ruler className="h-4 w-4" />
          ) : (
            <Wrench className="h-4 w-4" />
          ),
        count: filteredMeters.length,
        countClassName: selectedMeters[service] ? "bg-green-500" : undefined,
        loading: isLoading,
        component: (
          <div className="space-y-4">
            {/* Only show the resource box once a meter is actually selected;
                otherwise show the search table so the user can pick one. */}
            {connMeter && !isTableOpen ? (
              <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-lg space-y-3 shadow-2xs">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-blue-950">
                    Selected {service} Meter
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs border-blue-300 text-blue-800 hover:bg-blue-100 cursor-pointer"
                    disabled={isInstallationCompleted}
                    onClick={() =>
                      setShowTableMap((prev) => ({
                        ...prev,
                        [getConnKey(service)]: true,
                        [service]: true,
                      }))
                    }
                  >
                    Change Resource
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-1 text-xs">
                  <div>
                    <p className="text-muted-foreground font-medium">
                      Resource ID
                    </p>
                    <p className="font-bold text-gray-900 text-sm">
                      {connMeter.resourceId ||
                        connMeter.resource_id ||
                        connMeter.meterNumber ||
                        connMeter.meterNo ||
                        connMeter.meter_no ||
                        connMeter.meterId ||
                        "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground font-medium">
                      Serial No
                    </p>
                    <p className="font-bold text-gray-900 text-sm">
                      {connMeter.serialNo ||
                        connMeter.serial_no ||
                        connMeter.deviceNo ||
                        connMeter.deviceNumber ||
                        connMeter.device_no ||
                        "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground font-medium">
                      Physical Status
                    </p>
                    <p className="font-semibold text-gray-800">
                      {connMeter.physicalStatus ||
                        connMeter.physical_status ||
                        connMeter.physicalStatusDisplay ||
                        "Installed"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground font-medium mb-1">
                      Status
                    </p>
                    <Badge
                      variant="outline"
                      className="text-green-800 bg-green-100"
                    >
                      {connMeter.meterStatus ||
                        connMeter.meter_status ||
                        connMeter.statusDisplay ||
                        connMeter.status ||
                        "Assigned"}
                    </Badge>
                  </div>
                </div>
              </div>
            ) : (
              /* Available Meters Search & Table view */
              <>
                {/* Search Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium">
                      Search {service} Meters
                    </h3>
                    {connMeter && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-600 hover:text-gray-900 h-7"
                        onClick={() =>
                          setShowTableMap((prev) => ({
                            ...prev,
                            [getConnKey(service)]: false,
                            [service]: false,
                          }))
                        }
                      >
                        Close Table
                      </Button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={`Search by ${service.toLowerCase()} meter number or device number...`}
                      className="pl-8"
                      value={searchQueries[service] || ""}
                      onChange={(e) =>
                        handleSearchQueryChange(service, e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Meters Table */}
                <div>
                  <h3 className="text-sm font-medium mb-4">
                    Available {service} Meters
                  </h3>
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Resource ID</TableHead>
                          <TableHead>Serial No</TableHead>
                          <TableHead>Physical Status</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center py-4 text-muted-foreground"
                            >
                              Loading {service.toLowerCase()} resources...
                            </TableCell>
                          </TableRow>
                        ) : filteredMeters.length > 0 ? (
                          filteredMeters.map((rawMeter: any) => {
                            const meter = normalizeMeterResource(rawMeter);
                            const isConnSelected =
                              connMeter?.id === meter.id ||
                              connMeter?.meterId === meter.meterId ||
                              connMeter?.resourceId === meter.resourceId;

                            return (
                              <TableRow
                                key={meter.id || meter.resourceId}
                                className={cn(
                                  isConnSelected ? "bg-blue-50" : "",
                                )}
                              >
                                <TableCell>
                                  {meter.resourceId ||
                                    meter.resource_id ||
                                    meter.meterNumber ||
                                    meter.meterNo ||
                                    "-"}
                                </TableCell>
                                <TableCell>
                                  {meter.serialNo ||
                                    meter.serial_no ||
                                    meter.deviceNo ||
                                    meter.deviceNumber ||
                                    "-"}
                                </TableCell>
                                <TableCell>
                                  {meter.physicalStatus ||
                                    meter.physical_status ||
                                    meter.physicalStatusDisplay ||
                                    "Installed"}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className="text-green-800 bg-green-100"
                                  >
                                    {meter.meterStatus ||
                                      meter.meter_status ||
                                      meter.statusDisplay ||
                                      meter.status ||
                                      "Assigned"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isInstallationCompleted}
                                    onClick={() => {
                                      handleMeterSelect(service, meter);
                                      setShowTableMap((prev) => ({
                                        ...prev,
                                        [getConnKey(service)]: false,
                                        [service]: false,
                                      }));
                                    }}
                                  >
                                    {isConnSelected ? "Selected" : "Select"}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center py-4 text-muted-foreground"
                            >
                              {hasError
                                ? "Error loading meters"
                                : searchQueries[service]
                                  ? "No resources match your search"
                                  : "No resources available"}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </div>
        ),
        loadingComponent: (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
              <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        ),
      };
    });

    return config;
  }, [
    utilityServices,
    meterQueriesResults,
    searchQueries,
    selectedMeters,
    showTableMap,
    frozenMeters,
    meterMappingData,
    consumerDetailsData,
  ]);

  // Define form fields for scheduling installation with dynamic service orders
  const scheduleInstallationFields: FormField[] = useMemo(() => {
    return [
      {
        name: "selectedServiceOrder",
        label: "Select Service Order",
        type: "select",
        required: true,
        options: serviceOrderOptions,
        placeholder: isLoadingServiceOrders
          ? "Loading service orders..."
          : "Select service order",
        disabled: isLoadingServiceOrders,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "installationDate",
        label: "Select Installation Date",
        type: "datepicker",
        required: true,
        placeholder: "Select date",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "priority",
        label: "Select Priority",
        type: "select",
        required: true,
        options: priorityOptions,
        placeholder: "Select Priority",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "specialInstructions",
        label: "Special Instructions",
        type: "textarea",
        required: false,
        placeholder: "Add any special instructions for installation...",
        rows: 4,
        maxLength: 500,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          textarea:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue resize-none",
          error: "text-red-600 text-sm mt-1",
        },
      },
    ];
  }, [serviceOrderOptions, isLoadingServiceOrders]);

  // Create form using FormService
  const scheduleForm = FormService.createForm({
    fields: scheduleInstallationFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Reset scheduleForm whenever switching utility service tab or connection sub-tab
  useEffect(() => {
    scheduleForm.reset({
      selectedServiceOrder: "",
      installationDate: undefined,
      priority: "",
      specialInstructions: "",
    });
  }, [activeUtilityTab, activeConnectionTabs]);

  // Handle service orders loading error
  useEffect(() => {
    if (serviceOrdersError) {
      toast({
        title: "Error Loading Service Orders",
        description: "Failed to load service orders. Please try again.",
        variant: "destructive",
      });
    }
  }, [serviceOrdersError, toast]);

  // Handle meter queries errors
  useEffect(() => {
    utilityServices.forEach((service) => {
      const queryResult = meterQueriesResults[service];
      if (queryResult?.error) {
        toast({
          title: `Error Loading ${service} Meters`,
          description: `Failed to load ${service.toLowerCase()} meters. Please try again.`,
          variant: "destructive",
        });
      }
    });
  }, [meterQueriesResults, utilityServices, toast]);

  useEffect(() => {}, [acceptanceData, consumerDetailsData, application]);

  if (!application) {
    return <div>Loading...</div>;
  }

  // Handle schedule form submission
  const handleScheduleFormSubmit = async (formData: any) => {
    const { selectedServiceOrder, installationDate, specialInstructions } =
      formData;
    const currentTab = activeUtilityTab || utilityServices[0];
    const connQuantity = utilityServiceQuantityMapping[currentTab] || 1;
    const activeConnNum = activeConnectionTabs[currentTab] || 1;
    const connKey = getConnKey(currentTab, activeConnNum);

    try {
      const selectedOrder = serviceOrderOptions.find(
        (so) => so.value === selectedServiceOrder,
      );

      if (!selectedOrder) {
        toast({
          title: "Error",
          description: "Selected service order not found.",
          variant: "destructive",
        });
        return;
      }

      const formattedDate = format(
        new Date(installationDate),
        "yyyy-MM-dd HH:mm:ss.SSS xxxx",
      );

      // ✅ Fix: use .result for the actual data
      const consumerData =
        consumerDetailsData?.result || acceptanceData?.result || {};

      console.log("✅ consumerData at installation", consumerData);

      // ✅ Safely compute consumerFullName
      const consumerFullName = `${consumerData?.firstName ?? ""} ${
        consumerData?.lastName ?? ""
      }`.trim();

      const payload: any = {
        is_workorder: 1,
        remote_utility_id: remoteUtilityId,
        sop: parseInt(selectedServiceOrder),
        utility_service: currentTab,
        connection_number: activeConnNum,
        installation_methods: {
          [currentTab]: {
            method: "schedule_installation",
            sop_id: parseInt(selectedServiceOrder),
            connection_number: activeConnNum,
          },
        },
        request_account_no:
          consumerData.consumer_no ||
          consumerData.account_no ||
          consumerData.consumer_number ||
          application?.id ||
          "",
        request_id:
          consumerData.id ||
          consumerData.consumer_id ||
          consumerData.request_id ||
          parseInt(consumerId || "0"),
        request_username:
          consumerFullName ||
          consumerData.consumer_name ||
          consumerData.customer_name ||
          application?.customerName ||
          "",
        mobile_number:
          consumerData.contact_number ||
          consumerData.mobile_number ||
          consumerData.phone ||
          application?.customerPhone ||
          "",
        area: application?.addressData?.service?.AREA || "",
        sub_area: application?.addressData?.service?.SUBAREA || "",
        premise: application?.addressData?.service?.PREMISE || "",
        request_availability_date: formattedDate,
        remark: specialInstructions || "",
        priority: formData.priority,
      };

      console.log("🧾 installation payload", payload);

      await updateConsumerServiceMutation.mutateAsync(
        { consumerId, payload },
        { onSuccess: () => logTimeUntilStep("Installation") },
      );

      setServiceOrderDetails((prev) => ({
        ...prev,
        [connKey]: {
          ...selectedOrder.data,
          name: selectedOrder.label,
          installationDate,
          specialInstructions,
          status: "Scheduled",
        },
        [currentTab]: {
          ...selectedOrder.data,
          name: selectedOrder.label,
          installationDate,
          specialInstructions,
          status: "Scheduled",
        },
      }));

      setFrozenMeters((prev) => ({
        ...prev,
        [connKey]: true,
        [currentTab]: true,
      }));

      setShowServiceOrderDetails((prev) => ({ ...prev, [currentTab]: true }));

      toast({
        title: "Installation scheduled successfully",
        description: `Resource installation scheduled for ${format(
          new Date(installationDate),
          "PPP",
        )} for ${currentTab}${connQuantity > 1 ? ` (Connection ${activeConnNum})` : ""}.`,
      });

      scheduleForm.reset({
        selectedServiceOrder: "",
        installationDate: undefined,
        priority: "",
        specialInstructions: "",
      });

      // Auto advance
      if (activeConnNum < connQuantity) {
        const nextConn = activeConnNum + 1;
        setActiveConnectionTabs((prev) => ({
          ...prev,
          [currentTab]: nextConn,
        }));
        toast({
          title: `Moved to Connection ${nextConn}`,
          description: `Connection ${activeConnNum} scheduled. Please configure Connection ${nextConn}.`,
        });
      } else {
        const currentSvcIdx = utilityServices.indexOf(currentTab);
        if (currentSvcIdx >= 0 && currentSvcIdx < utilityServices.length - 1) {
          const nextUtility = utilityServices[currentSvcIdx + 1];
          setActiveUtilityTab(nextUtility);
          setActiveConnectionTabs((prev) => ({
            ...prev,
            [nextUtility]: 1,
          }));
          toast({
            title: `All connections scheduled for ${currentTab}`,
            description: `Moving to ${nextUtility} utility service.`,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error scheduling installation",
        description: "Failed to schedule the installation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAssignMeters = async () => {
    const currentTab = activeUtilityTab || utilityServices[0];
    if (!currentTab) return;

    const connQuantity = utilityServiceQuantityMapping[currentTab] || 1;
    const activeConnNum = activeConnectionTabs[currentTab] || 1;
    const connKey = getConnKey(currentTab, activeConnNum);

    const selectedMeter = getConnValue(
      selectedMeters,
      currentTab,
      activeConnNum,
    );
    const latestReading = getConnValue(
      latestReadings,
      currentTab,
      activeConnNum,
    );
    const readingDate = getConnValue(readingDates, currentTab, activeConnNum);

    if (!selectedMeter) {
      toast({
        title: "No resource selected",
        description: `Please select a meter for ${currentTab}${connQuantity > 1 ? ` (Connection ${activeConnNum})` : ""} to proceed.`,
        variant: "destructive",
      });
      return;
    }

    if (!latestReading || !readingDate) {
      toast({
        title: "Incomplete information",
        description: `Please provide resource reading information for ${currentTab}${connQuantity > 1 ? ` (Connection ${activeConnNum})` : ""}.`,
        variant: "destructive",
      });
      return;
    }

    try {
      // Format the date for API (ISO format) safely without crashing if readingDate is a string
      const formattedReadingDate =
        readingDate instanceof Date
          ? readingDate.toISOString()
          : typeof readingDate === "string" && readingDate
            ? new Date(readingDate).toISOString()
            : new Date().toISOString();

      const meterItem = {
        connection_number: activeConnNum,
        meter_id: selectedMeter.meterId || selectedMeter.id,
        meter_no:
          selectedMeter.meterNo ||
          selectedMeter.meterNumber ||
          selectedMeter.meter_no ||
          "",
        device_no:
          selectedMeter.deviceNo ||
          selectedMeter.deviceNumber ||
          selectedMeter.device_no ||
          "",
        current_reading: latestReading,
        current_reading_date: formattedReadingDate,
      };

      // Build payload ONLY for the current utility service being assigned
      const utilityServiceMeterData: Record<string, any> = {
        [currentTab]: [meterItem],
      };

      const payload = {
        is_metered: 1,
        utility_service: currentTab,
        utility_service_meter_data: utilityServiceMeterData,
      };

      // Make API call to update consumer installation status for this specific service
      try {
        await updateConsumerInstallationStatusMutation.mutateAsync({
          consumerId: consumerId!,
          payload: payload,
        });
      } catch (apiErr: any) {
        console.error("API updateConsumerInstallationStatus error:", apiErr);
        const errorMsg =
          apiErr?.response?.data?.message ||
          apiErr?.response?.data?.detail ||
          (typeof apiErr?.response?.data === "string"
            ? apiErr.response.data
            : null) ||
          apiErr?.message ||
          "Failed to update installation status";

        toast({
          title: "Error updating installation status",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      queryClient.refetchQueries({
        queryKey: ["cxonboarding", "consumerMeter"],
      });

      setFrozenMeters((prev) => ({
        ...prev,
        [connKey]: true,
        [currentTab]: true,
      }));

      toast({
        title: `${currentTab}${connQuantity > 1 ? ` Connection ${activeConnNum}` : ""} resource assigned`,
        description:
          "The resource has been assigned and installation status updated.",
      });

      // Auto Advance Logic:
      if (activeConnNum < connQuantity) {
        const nextConn = activeConnNum + 1;
        setActiveConnectionTabs((prev) => ({
          ...prev,
          [currentTab]: nextConn,
        }));
        toast({
          title: `Moved to Connection ${nextConn}`,
          description: `Connection ${activeConnNum} saved. Please configure Connection ${nextConn} for ${currentTab}.`,
        });
      } else {
        const currentSvcIdx = utilityServices.indexOf(currentTab);
        if (currentSvcIdx >= 0 && currentSvcIdx < utilityServices.length - 1) {
          const nextUtility = utilityServices[currentSvcIdx + 1];
          setActiveUtilityTab(nextUtility);
          setActiveConnectionTabs((prev) => ({
            ...prev,
            [nextUtility]: 1,
          }));
          toast({
            title: `All connections saved for ${currentTab}`,
            description: `Moving to ${nextUtility} utility service.`,
          });
        }
      }
    } catch (error) {
      console.error("Error assigning resources:", error);
    }
  };

  const handleAssignQuantity = () => {
    const currentTab = activeUtilityTab || utilityServices[0];
    if (!currentTab || !consumerId) return;

    const connQuantity = utilityServiceQuantityMapping[currentTab] || 1;
    const isOverrideChecked = !!overrideQuantities[currentTab];
    const rawCustom = customQuantities[currentTab];
    const effectiveQty =
      isOverrideChecked && rawCustom !== undefined && rawCustom !== ""
        ? rawCustom
        : connQuantity;

    const payload = {
      remote_utility_id: remoteUtilityId,
      utility_service: currentTab,
      quantity: Number(effectiveQty),
      action: "quantity_based",
    };

    updateQuantityConnectionMutation.mutate(
      {
        consumerId,
        remoteUtilityId,
        payload,
      },
      {
        onSuccess: () => {
          refetchConsumerDetails();
          setFrozenMeters((prev) => ({
            ...prev,
            [currentTab]: true,
          }));

          const currentSvcIdx = utilityServices.indexOf(currentTab);
          if (
            currentSvcIdx >= 0 &&
            currentSvcIdx < utilityServices.length - 1
          ) {
            const nextUtility = utilityServices[currentSvcIdx + 1];
            setActiveUtilityTab(nextUtility);
          }
        },
      },
    );
  };

  // Handle form array submission for Continue to Activation
  const handleFormArraySubmit = async () => {
    try {
      // Build the payload strictly from what's actually saved in
      // connectionDetailData — camelCase primary, snake_case fallback since
      // this API's nested fields arrive snake_case. A service with nothing
      // in connectionDetailData (e.g. Electricity in a scenario where only
      // Gas/Water have been touched) is simply skipped — never blocked on.
      const utilityServiceMeterData: Record<string, any> = {};
      const cdResult = connectionDetailData?.result;

      if (cdResult && typeof cdResult === "object") {
        Object.entries(cdResult).forEach(
          ([serviceKeyRaw, serviceData]: [string, any]) => {
            if (!serviceData || typeof serviceData !== "object") return;

            const matchedService =
              utilityServices.find(
                (s: string) => s.toLowerCase() === serviceKeyRaw.toLowerCase(),
              ) || serviceKeyRaw;

            // Skip quantity-based services entirely — they never contribute
            // to this payload.
            if (serviceData.action === "quantity_based") return;

            // Per-connection: build one entry per connectionInfo key that
            // actually has real meter data — no-meter connections and empty
            // keys are skipped, not included as placeholder entries.
            const connInfo =
              serviceData.connectionInfo || serviceData.connection_info;
            if (!connInfo || typeof connInfo !== "object") return;

            const entries: any[] = [];

            Object.entries(connInfo).forEach(
              ([connNumStr, info]: [string, any]) => {
                if (!info || typeof info !== "object") return;
                if (Object.keys(info).length === 0) return;
                if (info.action === "no_meter_connected") return;

                const connNum = Number(connNumStr);
                if (!Number.isFinite(connNum)) return;

                const meterId = info.meterId ?? info.meter_id;
                if (meterId === undefined || meterId === null) return;

                entries.push({
                  connection_number: connNum,
                  meter_id: meterId,
                  meter_no: info.meterNo || info.meter_no || "",
                  device_no: info.deviceNo || info.device_no || "",
                  current_reading:
                    info.currentReading ?? info.current_reading ?? "",
                  current_reading_date:
                    info.currentReadingDate || info.current_reading_date,
                });
              },
            );

            if (entries.length > 0) {
              utilityServiceMeterData[matchedService] = entries;
            }
          },
        );
      }

      const payload =
        Object.keys(utilityServiceMeterData).length > 0
          ? { utility_service_meter_data: utilityServiceMeterData }
          : {};

      // Make API call to complete installation
      // console.log("Submit Payload:", payload);
      // return;
      try {
        await installationCompletedMutation.mutateAsync({
          consumerId: consumerId!,
          payload: payload,
        });
      } catch (submitErr) {
        console.warn("installationCompletedMutation warning:", submitErr);
      }

      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      queryClient.refetchQueries({
        queryKey: ["cxonboarding", "consumerMeter"],
      });

      toast({
        title: "Installation data updated successfully",
        description:
          "Utility service data has been updated and ready for activation.",
      });

      navigate(
        `/cx/consumer-onboarding/workflow/activation/${consumerId}/ACTIVATION PENDING`,
      );
    } catch (error) {
      console.error("Error updating installation data:", error);
      navigate(
        `/cx/consumer-onboarding/workflow/activation/${consumerId}/ACTIVATION PENDING`,
      );
    }
  };

  const handleBack = () => {
    navigate(`/cx/consumer-onboarding/workflow/approval/${consumerId}`);
  };

  const handleContinueToActivationClick = () => {
    const pending = checkPendingConnections();
    if (pending.length > 0) {
      setPendingConnectionsList(pending);
      setShowWarningDialog(true);
      return;
    }
    handleFormArraySubmit();
  };

  const handleInstallationTypeChange = (
    cKey: string,
    currentSvc: string,
    value: string,
    connNum?: number,
  ) => {
    setInstallationTypes((prev) => ({
      ...prev,
      [cKey]: value as any,
    }));

    // ONLY call API when "No Resource Connected" (no-meter) radio button is clicked
    if (value === "no-meter" && consumerId) {
      const existingNonMeter: string[] =
        consumerDetailsData?.result?.additionalData?.nonMeterServices ||
        consumerDetailsData?.result?.additional_data?.non_meter_services ||
        [];
      const updatedNonMeterServices = Array.from(
        new Set([...existingNonMeter, currentSvc]),
      );

      const payload: any = {
        additional_data: {
          non_meter_services: updatedNonMeterServices,
          ...(connNum ? { connection_number: connNum } : {}),
        },
      };

      updateConsumerMutation.mutate({
        consumerId: consumerId,
        payload,
      });
    }
  };

  const renderWorkflowFooter = (
    actionButton?: React.ReactNode,
    continueButton?: React.ReactNode,
  ) => (
    <div className="space-y-6 mt-4">
      {actionButton && <div className="flex justify-end">{actionButton}</div>}

      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={handleBack}
          type="button"
          className="h-11"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex justify-end">
          {continueButton || (
            <Button
              onClick={handleContinueToActivationClick}
              isLoading={installationCompletedMutation.isPending}
              disabled={
                installationCompletedMutation.isPending ||
                application?.progressStatusDisplay === "Hold"
              }
            >
              Continue to activation
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const isEmptyObject = (obj) =>
    obj && Object.keys(obj).length === 0 && obj.constructor === Object;

  const handleAcknowledge = () => {
    // update the status in the database
    const acknowledge_date = new Date().toISOString();
    updateStatus.mutate({
      remote_utility_id: remoteUtilityId,
      progress_status: "IN_PROGRESS",
      acknowledge_date,
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "HOLD",
      });

      toast({
        title: "Application on hold",
        description: "The application has been put on hold.",
      });
    } catch (error) {
      console.error("Error putting application on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put application on hold. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResume = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
      });

      toast({
        title: "Application resumed",
        description: "The application has been resumed and is now in progress.",
      });
    } catch (error) {
      console.error("Error resuming application:", error);
      toast({
        title: "Error",
        description: "Failed to resume application. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay),
  );

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/cx/consumer-onboarding")}
            className="hover:bg-gray-100 rounded-full h-10 w-10"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-2xl font-bold">New Service Connection Details</h1>
        </div>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <WorkflowProgress
        steps={workflowSteps}
        currentStepId={application.currentStep}
        status={activeConnectionServiceOrder?.statusDisplay}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div>
                <div className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-workflow-blue" />
                  <CardTitle>Resource Installation</CardTitle>
                </div>
                <CardDescription className="ml-7">
                  Schedule a new resource installation or assign an existing
                  resource
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="mt-4">
              {/* Handling Loading States */}
              {isLoadingConnectionDetail || isLoadingConsumerDetails ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-workflow-blue mb-4"></div>
                  <p className="text-gray-500 font-medium">
                    Synchronizing service data...
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Persistent Navigation and Information Section */}
                  <div className="space-y-6">
                    {/* Information message explaining assignment status */}
                    {hasUnassignedMeters &&
                      meterMappingData.length > 0 &&
                      !showServiceOrderDetails[
                        activeUtilityTab || utilityServices[0]
                      ] && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <Info className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Partial Meter Assignment Required
                              </h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>
                                  Some utility services already have resources
                                  assigned, but there are still services that
                                  need resource installation. Please complete
                                  the resource assignment for the remaining
                                  services below.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* ALWAYS Persistent Utility Tabs for Navigation */}
                    {utilityServices.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-medium mb-2 text-muted-foreground">
                          Utility Service
                        </h3>
                        <Tabs
                          defaultValue={activeUtilityTab || utilityServices[0]}
                          value={activeUtilityTab || utilityServices[0]}
                          onValueChange={(val) => {
                            setActiveUtilityTab(val);
                            const searchParams = new URLSearchParams(
                              location.search,
                            );
                            searchParams.set("tab", val);
                            navigate(
                              `${location.pathname}?${searchParams.toString()}`,
                              {
                                replace: true,
                              },
                            );
                          }}
                          disableUrlSync={false}
                          tabComponents={utilityServices.reduce(
                            (acc: Record<string, any>, service: string) => {
                              const isAssigned = serviceCompletionMap[service];
                              acc[service] = {
                                label: service,
                                component: null,
                                className: isAssigned
                                  ? "bg-green-50 shadow-sm"
                                  : undefined,
                                count: isAssigned ? "✓" : undefined,
                                countClassName:
                                  "bg-green-500 text-white border-none",
                              };
                              return acc;
                            },
                            {},
                          )}
                          tabsListClassName={`grid w-full grid-cols-${
                            utilityServices.length > 0
                              ? utilityServices.length
                              : 1
                          }`}
                        />
                      </div>
                    )}
                  </div>

                  {/* UNIFIED UTILITY & CONNECTION RENDERER */}
                  {(() => {
                    const currentSvc =
                      activeUtilityTab || utilityServices[0] || "Utility";
                    const connQuantity =
                      utilityServiceQuantityMapping[currentSvc] || 1;
                    const activeConnNum = activeConnectionTabs[currentSvc] || 1;
                    const cKey = getConnKey(currentSvc, activeConnNum);
                    const currentMeasurement =
                      measurementMethods[currentSvc] || "meter";

                    // Method-specific: only true when THIS connection's method
                    // (from connectionDetailData) or local session state says so,
                    // so an "assign_existing_meter" connection never shows
                    // another connection's Service Order Tracking details.
                    const isConnScheduled =
                      connectionMethodMap[cKey] === "schedule" ||
                      !!serviceOrderDetails[cKey] ||
                      (activeConnNum === 1 &&
                        connectionMethodMap[currentSvc] === undefined &&
                        !!serviceOrderDetails[currentSvc]);

                    return (
                      <div className="mb-6 space-y-6">
                        {/* USAGE MEASUREMENT METHOD - always editable until the
                            user clicks "Continue to activation" */}
                        {
                          <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
                              Usage measurement method
                            </h3>
                            <RadioGroup
                              value={currentMeasurement}
                              onValueChange={(value) => {
                                setMeasurementMethods((prev) => ({
                                  ...prev,
                                  [currentSvc]: value as "meter" | "quantity",
                                }));
                              }}
                              className="flex flex-col md:flex-row gap-4"
                            >
                              <label
                                htmlFor={`meter-based-${currentSvc}`}
                                onClick={() => {
                                  setMeasurementMethods((prev) => ({
                                    ...prev,
                                    [currentSvc]: "meter",
                                  }));
                                }}
                                className={cn(
                                  "flex items-start space-x-3 border rounded-xl p-4 cursor-pointer flex-1 transition-all select-none",
                                  currentMeasurement === "meter"
                                    ? "border-blue-500 bg-blue-50/40 ring-1 ring-blue-500"
                                    : "border-gray-200 hover:border-gray-300",
                                )}
                              >
                                <RadioGroupItem
                                  value="meter"
                                  id={`meter-based-${currentSvc}`}
                                  className="mt-0.5"
                                />
                                <div>
                                  <span className="font-semibold text-gray-900 cursor-pointer block text-sm">
                                    Resource based
                                  </span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Install or assign a physical resource per
                                    connection
                                  </p>
                                </div>
                              </label>

                              <label
                                htmlFor={`quantity-based-${currentSvc}`}
                                onClick={() => {
                                  setMeasurementMethods((prev) => ({
                                    ...prev,
                                    [currentSvc]: "quantity",
                                  }));
                                }}
                                className={cn(
                                  "flex items-start space-x-3 border rounded-xl p-4 cursor-pointer flex-1 transition-all select-none",
                                  currentMeasurement === "quantity"
                                    ? "border-blue-500 bg-blue-50/40 ring-1 ring-blue-500"
                                    : "border-gray-200 hover:border-gray-300",
                                )}
                              >
                                <RadioGroupItem
                                  value="quantity"
                                  id={`quantity-based-${currentSvc}`}
                                  className="mt-0.5"
                                />
                                <div>
                                  <span className="font-semibold text-gray-900 cursor-pointer block text-sm">
                                    Quantity based
                                  </span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Bill by number of connections — no resource
                                    installation required
                                  </p>
                                </div>
                              </label>
                            </RadioGroup>
                          </div>
                        }

                        {/* QUANTITY BASED VIEW */}
                        {currentMeasurement === "quantity" ? (
                          <div className="space-y-6">
                            <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-5 space-y-4">
                              <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm">
                                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                                <span>
                                  Quantity based — no resource installation
                                  required
                                </span>
                              </div>

                              {(() => {
                                const isOverrideChecked =
                                  !!overrideQuantities[currentSvc];
                                const effectiveQty =
                                  isOverrideChecked &&
                                  customQuantities[currentSvc] !== undefined
                                    ? customQuantities[currentSvc]
                                    : connQuantity;

                                return (
                                  <>
                                    <div className="bg-white border border-gray-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-3 gap-6 shadow-2xs">
                                      <div>
                                        <p className="text-xs font-semibold text-gray-400 tracking-wider mb-1">
                                          Utility
                                        </p>
                                        <p className="text-sm font-bold text-gray-900">
                                          {currentSvc}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-xs font-semibold text-gray-400 tracking-wider mb-1">
                                          Connections requested
                                        </p>
                                        <p className="text-2xl font-bold text-blue-600">
                                          {connQuantity ?? "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-xs font-semibold text-gray-400 tracking-wider mb-1">
                                          Total quantity
                                        </p>
                                        <p className="text-2xl font-bold text-blue-600">
                                          {effectiveQty !== "" &&
                                          effectiveQty !== undefined
                                            ? `${effectiveQty} units`
                                            : "N/A"}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="space-y-3 pt-2">
                                      <div className="flex items-center space-x-2.5">
                                        <input
                                          type="checkbox"
                                          id={`override-qty-${currentSvc}`}
                                          checked={isOverrideChecked}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setOverrideQuantities((prev) => ({
                                              ...prev,
                                              [currentSvc]: isChecked,
                                            }));
                                            if (
                                              isChecked &&
                                              customQuantities[currentSvc] ===
                                                undefined
                                            ) {
                                              setCustomQuantities((prev) => ({
                                                ...prev,
                                                [currentSvc]: connQuantity || 1,
                                              }));
                                            }
                                          }}
                                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        />
                                        <label
                                          htmlFor={`override-qty-${currentSvc}`}
                                          className="text-xs font-semibold text-gray-800 cursor-pointer block"
                                        >
                                          Override quantity
                                        </label>
                                      </div>

                                      {isOverrideChecked && (
                                        <div className="flex items-center gap-2 pl-6">
                                          <Input
                                            type="number"
                                            min={1}
                                            value={
                                              customQuantities[currentSvc] !==
                                              undefined
                                                ? customQuantities[currentSvc]
                                                : connQuantity || 1
                                            }
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setCustomQuantities((prev) => ({
                                                ...prev,
                                                [currentSvc]: val,
                                              }));
                                            }}
                                            className="w-24 h-9 text-sm font-semibold border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                                          />
                                          <span className="text-xs text-gray-500 font-medium">
                                            units{" "}
                                            <span className="text-gray-400">
                                              (requested: {connQuantity})
                                            </span>
                                          </span>
                                        </div>
                                      )}

                                      <p className="text-xs text-gray-500">
                                        Each connection is billed at a fixed
                                        quantity rate. No physical meter needs
                                        to be installed or assigned.
                                      </p>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>

                            <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/30 flex items-center gap-2 text-blue-700 text-xs font-medium">
                              <Info className="h-4 w-4 flex-shrink-0 text-blue-500" />
                              <span>
                                The selected utility resource installation must
                                be completed within 7 days of approval.
                              </span>
                            </div>

                            {renderWorkflowFooter(
                              <Button
                                onClick={handleAssignQuantity}
                                isLoading={
                                  updateQuantityConnectionMutation.isPending
                                }
                                disabled={
                                  updateQuantityConnectionMutation.isPending ||
                                  application?.progressStatusDisplay === "Hold"
                                }
                              >
                                Add Quantity
                              </Button>,
                            )}
                          </div>
                        ) : (
                          /* METER BASED / SAVED VIEW */
                          <div className="space-y-6">
                            {/* CONNECTION SUB TABS - USING PLATFORM TABS COMPONENT */}
                            {connQuantity > 1 && (
                              <div className="space-y-2">
                                <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
                                  Connection
                                </h3>
                                <Tabs
                                  value={String(activeConnNum)}
                                  onValueChange={(val) =>
                                    setActiveConnectionTabs((prev) => ({
                                      ...prev,
                                      [currentSvc]: Number(val),
                                    }))
                                  }
                                  disableUrlSync={true}
                                  level={1}
                                  tabComponents={Array.from(
                                    { length: connQuantity },
                                    (_, idx) => idx + 1,
                                  ).reduce(
                                    (acc: Record<string, any>, connNum) => {
                                      const checkKey = getConnKey(
                                        currentSvc,
                                        connNum,
                                      );
                                      const isConnDone =
                                        connectionCompletionMap[checkKey];

                                      acc[String(connNum)] = {
                                        label: `Connection ${connNum}`,
                                        component: null,
                                        count: isConnDone ? "✓" : undefined,
                                        countClassName:
                                          "bg-green-500 text-white border-none",
                                      };
                                      return acc;
                                    },
                                    {},
                                  )}
                                  tabsListClassName={`grid w-full grid-cols-${connQuantity}`}
                                />
                              </div>
                            )}

                            {/* PER-CONNECTION VIEW AREA — radios and inputs stay
                                editable until the user clicks "Continue to
                                activation"; nothing is locked into a read-only
                                summary before then. */}
                            <div className="space-y-6">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                                  Resource installation for {currentSvc}
                                  {connQuantity > 1
                                    ? ` – Connection ${activeConnNum}`
                                    : ""}
                                </h3>
                                <RadioGroup
                                  value={installationTypes[cKey] || "schedule"}
                                  onValueChange={(value) =>
                                    handleInstallationTypeChange(
                                      cKey,
                                      currentSvc,
                                      value,
                                      activeConnNum,
                                    )
                                  }
                                  className="flex flex-col md:flex-row gap-4"
                                >
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                      value="schedule"
                                      id={`schedule-${cKey}`}
                                    />
                                    <Label htmlFor={`schedule-${cKey}`}>
                                      Schedule New Installation
                                    </Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                      value="assign"
                                      id={`assign-${cKey}`}
                                    />
                                    <Label htmlFor={`assign-${cKey}`}>
                                      Assign Existing Resource
                                    </Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                      value="no-meter"
                                      id={`no-meter-${cKey}`}
                                    />
                                    <Label htmlFor={`no-meter-${cKey}`}>
                                      No Resource Connected
                                    </Label>
                                  </div>
                                </RadioGroup>
                              </div>

                              {(installationTypes[cKey] || "schedule") ===
                              "schedule" ? (
                                <div className="space-y-6">
                                  {/* Service Order Tracking Details — only
                                        renders once THIS connection actually
                                        has data (isConnScheduled true). Before
                                        that, the form below is shown instead;
                                        the panel "arrives" right after the
                                        user schedules it. All fields come from
                                        serviceOrderDetails[cKey] (parsed from
                                        connectionDetailData). */}
                                  {isConnScheduled && (
                                    <div className="mt-2">
                                      <h3 className="text-lg font-medium mb-4">
                                        Service Order Tracking Details (
                                        {currentSvc} – Connection{" "}
                                        {activeConnNum})
                                      </h3>
                                      <div className="bg-muted/50 rounded-lg p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            <p className="text-sm font-medium">
                                              Service Order
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.name || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Status
                                            </p>
                                            <Badge
                                              variant="outline"
                                              className="mt-1"
                                            >
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.statusDisplay || "N/A"
                                                : "Not scheduled"}
                                            </Badge>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Priority
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.priorityDisplay || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Job Card No
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.jobCardNo || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Created On
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.createdDate ||
                                                  format(new Date(), "PPP")
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Type
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.sopTypeDisplay || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Subtype
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.sopSubtypeDisplay || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Assigned On
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.assignDate ||
                                                  "Not assigned"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Field Force
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.fieldForce?.name ||
                                                  "Not assigned"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Expected End Date
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.expectedEndDate ||
                                                  "Not set"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Request Availability Date
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.requestAvailabilityDate ||
                                                  "Not set"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Account Number
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.requestAccountNo || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Customer Name
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.requestUsername || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Mobile Number
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.mobileNumber || "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Area
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.territoryData?.area ||
                                                  "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Sub Area
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.territoryData?.subArea ||
                                                  "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">
                                              Premise
                                            </p>
                                            <p className="text-sm">
                                              {isConnScheduled
                                                ? serviceOrderDetails[cKey]
                                                    ?.territoryData?.premise ||
                                                  "N/A"
                                                : "-"}
                                            </p>
                                          </div>
                                        </div>

                                        <div className="flex gap-3 pt-6 mt-6 border-t">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleViewReport}
                                            disabled={
                                              !isConnScheduled ||
                                              !reportId ||
                                              viewReport.isPending
                                            }
                                          >
                                            <FileSearch className="h-4 w-4 mr-2" />{" "}
                                            View Report
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleDownloadReport}
                                            disabled={
                                              !isConnScheduled ||
                                              !reportId ||
                                              downloadReport.isPending
                                            }
                                          >
                                            <Download className="h-4 w-4 mr-2" />{" "}
                                            Download PDF
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Resource assignment form — appears
                                        directly below the tracking panel once
                                        THIS connection's service order is
                                        Approved. Does NOT switch the radio to
                                        "assign"; it renders inline here so the
                                        connection stays on "Schedule New
                                        Installation" the whole time. */}
                                  {serviceOrderDetails[cKey]?.statusDisplay ===
                                    "Approved" && (
                                    <div className="space-y-6 pt-2 border-t">
                                      <h3 className="text-sm font-semibold text-gray-900 pt-4">
                                        Assign Installed Resource for{" "}
                                        {currentSvc}
                                        {connQuantity > 1
                                          ? ` – Connection ${activeConnNum}`
                                          : ""}
                                      </h3>
                                      {utilityTabsConfig[currentSvc]?.component}
                                      {!!getConnValue(
                                        selectedMeters,
                                        currentSvc,
                                        activeConnNum,
                                      ) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t mt-6">
                                          <div>
                                            <Label
                                              htmlFor="approvedLatestReading"
                                              className="mb-2 block"
                                            >
                                              Latest Meter Reading
                                            </Label>
                                            <Input
                                              id="approvedLatestReading"
                                              placeholder="Enter current meter reading"
                                              value={
                                                getConnValue(
                                                  latestReadings,
                                                  currentSvc,
                                                  activeConnNum,
                                                ) || ""
                                              }
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                setLatestReadings((prev) => ({
                                                  ...prev,
                                                  [cKey]: val,
                                                  [currentSvc]: val,
                                                }));
                                              }}
                                              type="number"
                                            />
                                          </div>
                                          <div>
                                            <Label
                                              htmlFor="approvedReadingDate"
                                              className="mb-2 block"
                                            >
                                              Reading Date
                                            </Label>
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <Button
                                                  variant="outline"
                                                  className={cn(
                                                    "w-full justify-start text-left font-normal",
                                                    !getConnValue(
                                                      readingDates,
                                                      currentSvc,
                                                      activeConnNum,
                                                    ) &&
                                                      "text-muted-foreground",
                                                  )}
                                                >
                                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                                  {getConnValue(
                                                    readingDates,
                                                    currentSvc,
                                                    activeConnNum,
                                                  ) ? (
                                                    normalizeDate(
                                                      getConnValue(
                                                        readingDates,
                                                        currentSvc,
                                                        activeConnNum,
                                                      )!.toISOString(),
                                                    )
                                                  ) : (
                                                    <span>Select date</span>
                                                  )}
                                                </Button>
                                              </PopoverTrigger>
                                              <PopoverContent
                                                className="w-auto p-0"
                                                align="start"
                                              >
                                                <Calendar
                                                  mode="single"
                                                  selected={getConnValue(
                                                    readingDates,
                                                    currentSvc,
                                                    activeConnNum,
                                                  )}
                                                  onSelect={(date) => {
                                                    setReadingDates((prev) => ({
                                                      ...prev,
                                                      [cKey]: date,
                                                      [currentSvc]: date,
                                                    }));
                                                  }}
                                                  disabled={(date) =>
                                                    date > new Date()
                                                  }
                                                  initialFocus
                                                  className="p-3 pointer-events-auto"
                                                />
                                              </PopoverContent>
                                            </Popover>
                                          </div>
                                        </div>
                                      )}
                                      {renderWorkflowFooter(
                                        <Button
                                          onClick={handleAssignMeters}
                                          isLoading={
                                            updateConsumerInstallationStatusMutation.isPending
                                          }
                                          disabled={
                                            updateConsumerInstallationStatusMutation.isPending ||
                                            application?.progressStatusDisplay ===
                                              "Hold"
                                          }
                                        >
                                          Assign Resource
                                        </Button>,
                                      )}
                                    </div>
                                  )}

                                  {/* Only show the schedule form when THIS
                                        connection has no existing data in
                                        connectionDetailData's connectionInfo
                                        yet (isConnScheduled false). Once data
                                        exists, the tracking panel above is
                                        enough — don't also show a blank form. */}
                                  {!isConnScheduled ? (
                                    <DynamicForm
                                      fields={scheduleInstallationFields}
                                      form={scheduleForm}
                                      onSubmit={handleScheduleFormSubmit}
                                      renderCustomFooter={() =>
                                        renderWorkflowFooter(
                                          <Button
                                            onClick={() => {
                                              const form =
                                                document.querySelector("form");
                                              if (form) form.requestSubmit();
                                            }}
                                            isLoading={
                                              updateConsumerServiceMutation.isPending
                                            }
                                            disabled={
                                              updateConsumerServiceMutation.isPending ||
                                              application?.progressStatusDisplay ===
                                                "Hold"
                                            }
                                          >
                                            Schedule installation
                                          </Button>,
                                        )
                                      }
                                      config={{
                                        layout: "vertical",
                                        spacing: "medium",
                                        showResetButton: false,
                                        classes: {
                                          form: "space-y-6",
                                          fieldContainer: "w-full",
                                          buttonContainer: "hidden",
                                        },
                                      }}
                                    />
                                  ) : serviceOrderDetails[cKey]
                                      ?.statusDisplay !== "Approved" ? (
                                    renderWorkflowFooter()
                                  ) : null}
                                </div>
                              ) : installationTypes[cKey] === "assign" ? (
                                <div className="space-y-6">
                                  {utilityTabsConfig[currentSvc]?.component}
                                  {!!getConnValue(
                                    selectedMeters,
                                    currentSvc,
                                    activeConnNum,
                                  ) &&
                                    (() => {
                                      const selectedMeterObj = getConnValue(
                                        selectedMeters,
                                        currentSvc,
                                        activeConnNum,
                                      );
                                      return (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t mt-6">
                                          <div>
                                            <Label
                                              htmlFor="latestReading"
                                              className="mb-2 block"
                                            >
                                              Latest Reading
                                            </Label>
                                            <Input
                                              id="latestReading"
                                              placeholder="Enter current reading"
                                              value={
                                                getConnValue(
                                                  latestReadings,
                                                  currentSvc,
                                                  activeConnNum,
                                                ) || ""
                                              }
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                setLatestReadings((prev) => ({
                                                  ...prev,
                                                  [cKey]: val,
                                                  [currentSvc]: val,
                                                }));
                                              }}
                                              type="number"
                                            />
                                          </div>
                                          <div>
                                            <Label
                                              htmlFor="readingDate"
                                              className="mb-2 block"
                                            >
                                              Reading Date
                                            </Label>
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <Button
                                                  variant="outline"
                                                  className={cn(
                                                    "w-full justify-start text-left font-normal",
                                                    !getConnValue(
                                                      readingDates,
                                                      currentSvc,
                                                      activeConnNum,
                                                    ) &&
                                                      "text-muted-foreground",
                                                  )}
                                                >
                                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                                  {getConnValue(
                                                    readingDates,
                                                    currentSvc,
                                                    activeConnNum,
                                                  ) ? (
                                                    normalizeDate(
                                                      getConnValue(
                                                        readingDates,
                                                        currentSvc,
                                                        activeConnNum,
                                                      )!.toISOString(),
                                                    )
                                                  ) : (
                                                    <span>Select date</span>
                                                  )}
                                                </Button>
                                              </PopoverTrigger>
                                              <PopoverContent
                                                className="w-auto p-0"
                                                align="start"
                                              >
                                                <Calendar
                                                  mode="single"
                                                  selected={getConnValue(
                                                    readingDates,
                                                    currentSvc,
                                                    activeConnNum,
                                                  )}
                                                  onSelect={(date) => {
                                                    setReadingDates((prev) => ({
                                                      ...prev,
                                                      [cKey]: date,
                                                      [currentSvc]: date,
                                                    }));
                                                  }}
                                                  disabled={(date) =>
                                                    date > new Date()
                                                  }
                                                  initialFocus
                                                  className="p-3 pointer-events-auto"
                                                />
                                              </PopoverContent>
                                            </Popover>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  {renderWorkflowFooter(
                                    <Button
                                      onClick={handleAssignMeters}
                                      isLoading={
                                        updateConsumerInstallationStatusMutation.isPending
                                      }
                                      disabled={
                                        updateConsumerInstallationStatusMutation.isPending ||
                                        application?.progressStatusDisplay ===
                                          "Hold"
                                      }
                                    >
                                      Assign Resource
                                    </Button>,
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-6">
                                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                    <p className="text-gray-600">
                                      <span className="font-semibold text-blue-600 bg-blue-100 px-1 rounded">
                                        No Resource
                                      </span>{" "}
                                      is required for this connection. You can
                                      proceed directly to account activation.
                                    </p>
                                  </div>
                                  {renderWorkflowFooter(
                                    undefined,
                                    <Button
                                      onClick={handleContinueToActivationClick}
                                      isLoading={
                                        installationCompletedMutation.isPending
                                      }
                                      disabled={
                                        installationCompletedMutation.isPending ||
                                        application?.progressStatusDisplay ===
                                          "Hold"
                                      }
                                    >
                                      Continue to activation
                                      <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>,
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Meter Summary Card on the Right Side */}
        <div className="lg:col-span-1">
          {/* Installation Status Card */}
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Installation Status</CardTitle>
              <CardDescription>
                Track resource connection status by utility service
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {utilityServices.map((service: string) => {
                  const connCount = utilityServiceQuantityMapping[service] || 1;
                  const isQuantityMethod =
                    measurementMethods[service] === "quantity";

                  if (isQuantityMethod) {
                    const isQuantityAdded = !!frozenMeters[service];
                    const rawCustom = customQuantities[service];
                    const effectiveQty =
                      overrideQuantities[service] &&
                      rawCustom !== undefined &&
                      rawCustom !== ""
                        ? rawCustom
                        : connCount;

                    return (
                      <div
                        key={service}
                        className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm flex items-center justify-between"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">
                            {service}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {isQuantityAdded
                              ? `Quantity added — ${effectiveQty} units`
                              : "Quantity based — pending addition"}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            "whitespace-nowrap ml-2 font-medium text-xs px-3 py-1 rounded-full",
                            isQuantityAdded
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-orange-50 text-orange-600 border-orange-200",
                          )}
                        >
                          {isQuantityAdded
                            ? "Quantity added"
                            : "Pending quantity"}
                        </Badge>
                      </div>
                    );
                  }

                  return Array.from(
                    { length: connCount },
                    (_, idx) => idx + 1,
                  ).map((connNum) => {
                    const cKey = getConnKey(service, connNum);
                    const isCurrentServiceActive =
                      (activeUtilityTab || utilityServices[0]) === service;

                    const currentType =
                      connCount > 1
                        ? installationTypes[cKey]
                        : installationTypes[cKey] || installationTypes[service];

                    const selectedMeterObj =
                      connCount > 1
                        ? selectedMeters[cKey]
                        : selectedMeters[cKey] || selectedMeters[service];

                    const serviceKey = service.toLowerCase();
                    const mapping =
                      connCount === 1
                        ? meterMappingData?.find(
                            (m: any) =>
                              m.utilityService === service && m.meterDetails,
                          )
                        : null;
                    const additionalMeterData =
                      connCount === 1
                        ? consumerDetailsData?.result?.additionalData
                            ?.installationMeterData?.utilityServiceMeterData?.[
                            serviceKey
                          ]
                        : null;

                    const meterInfo =
                      selectedMeterObj ||
                      mapping?.meterDetails ||
                      additionalMeterData;

                    const connSO =
                      serviceOrderDetails[cKey] ||
                      (connNum === 1 ? serviceOrderDetails[service] : null);

                    let statusBadgeText = "Pending assignment";
                    let subText = "Pending Resource assignment";
                    let badgeClass =
                      "bg-orange-50 text-orange-600 border-orange-200";

                    if (currentType === "no-meter") {
                      statusBadgeText = "No resource connected";
                      subText =
                        "No resource required or connected for this service";
                      badgeClass = "bg-gray-50 text-gray-600 border-gray-200";
                    } else if (
                      meterInfo ||
                      frozenMeters[cKey] ||
                      frozenMeters[service] ||
                      connectionCompletionMap[cKey]
                    ) {
                      const meterNo =
                        meterInfo?.meterNo ||
                        meterInfo?.meterNumber ||
                        meterInfo?.meter_no ||
                        meterInfo?.id ||
                        "";
                      const deviceNo =
                        meterInfo?.deviceNo ||
                        meterInfo?.deviceNumber ||
                        meterInfo?.device_no ||
                        "";
                      statusBadgeText = "Resource assigned";
                      subText =
                        meterNo && deviceNo
                          ? `Selected: ${meterNo} (${deviceNo})`
                          : meterNo
                            ? `Selected: ${meterNo}`
                            : "Resource assigned";
                      badgeClass =
                        "bg-green-50 text-green-700 border-green-200";
                    } else if (connSO) {
                      statusBadgeText = "Service order created";
                      subText =
                        connSO?.name || "New installation has been scheduled";
                      badgeClass =
                        "bg-amber-50 text-amber-700 border-amber-200";
                    }

                    return (
                      <div
                        key={`${service}-conn-${connNum}`}
                        className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm flex items-center justify-between"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">
                            {service}
                            {connCount > 1 ? ` – Connection ${connNum}` : ""}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {subText}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            "whitespace-nowrap ml-2 font-medium text-xs px-3 py-1 rounded-full",
                            badgeClass,
                          )}
                        >
                          {statusBadgeText}
                        </Badge>
                      </div>
                    );
                  });
                })}
              </div>
            </CardContent>
          </Card>

          <TimelineTab
            remoteUtilityId={remoteUtilityId}
            entityId={consumerId}
            actionType={"nsc"}
          />
        </div>
      </div>

      <UnsavedConnectionsWarningDialog
        open={showWarningDialog}
        onOpenChange={setShowWarningDialog}
        pendingConnections={pendingConnectionsList}
        onConfirm={() => {
          setShowWarningDialog(false);
          handleFormArraySubmit();
        }}
      />
    </>
  );
};

export default InstallationPage;
